/**
 * Every surface and piece of equipment in a food operation: how often, with what, step by step, what can go wrong
 * (contingencies) and the easy fix ("Do X", "Buy Y"). Content comes only from docs/source and the Food Code sections
 * it cites. Where the sources are silent about a specific machine, the entry says to follow the maker's instructions
 * and applies the general Food Code cleaning rules (4-601.11 clean to sight and touch, 4-602.11 frequencies, 4-603 methods).
 * The content agent fills SURFACES; this shape is the contract the apps and the Ask helper build against.
 *
 * Citation style: "Food Code x-xxx.xx" for code sections the sources quote; "Source protocols: <section> (<row>)" for a
 * table row or passage in docs/source/*-protocol.md; "Research: <file> (<row>)" for docs/source/research/*.md.
 * Brand names are examples named in the sources, not endorsements.
 */

import { SURFACES_UT } from "./states/ut/surfaces.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { perState, wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof SURFACES_UT>> = perStateTable("SURFACES", SURFACES_UT);
/** A citation a state may not have: one entry, or none. */
const one = (c: string | null): string[] => (c ? [c] : []);

export type SurfaceArea = "coffee_bar" | "cooking_line" | "prep" | "dish" | "cold_storage" | "dry_storage" | "front_of_house" | "restroom" | "floors_walls" | "trash_outside" | "bar_beverage";
export type SurfaceKind = "food_contact" | "non_food_contact" | "equipment" | "touch_point" | "handwashing";

export interface ProductPick {
  /** The catalog purpose (products.ts): the one place its buy link, rules and a place's own product live. */
  productId?: import("./products.ts").ProductPurposeId;
  /** What the product is for, e.g. "Espresso backflush and portafilter soak". */
  purpose: string;
  /** Example brands named in the sources (not endorsements). */
  examples: string[];
  /** Links to a supply key (actions.ts SUPPLY_SEEDS / location supplies) so "Buy" becomes a reorder. */
  supplyKey?: string;
  note?: string;
}

export interface Contingency {
  /** The situation, in plain words: "Raw meat touched it", "The wood board has deep cuts". */
  if: string;
  /** What to do, as short imperative steps: "Wash, rinse and sanitize before cutting vegetables". */
  then: string[];
  citations: string[];
}

export interface SurfaceGuide {
  id: string; // slug, stable
  /** A how-to-read guide, not a thing you clean ("Upholstery cleaning codes on the tag"): Ask and the library show it, but it is
   *  never a room-check item or a suggested object (owner, 2026-10-08: "Is the upholstery cleaning codes on the tag clear and wiped? what is dis"). */
  reference?: true;
  name: string; // "Wood cutting board"
  area: SurfaceArea;
  kind: SurfaceKind;
  /** Materials this covers (e.g. ["wood"], ["stainless steel"]). */
  materials?: string[];
  /** When to clean or sanitize, each with its source. */
  howOften: { when: string; citation: string }[];
  /** Step-by-step, imperative, 6th-grade words. */
  steps: string[];
  products: ProductPick[];
  contingencies: Contingency[];
  /** Mistakes to avoid: "Don't soak a wood board", "Never use the restroom mop here". */
  dont: string[];
  citations: string[];
  keywords: string[];
  /**
   * Leather, vinyl and cloth seating: the maker decides what touches it. The SOP view (sop.ts) leaves out the
   * food-contact sanitizer line and the sanitizer-bucket cloth storage for these, because sanitizer and bleach can damage them.
   */
  softSurface?: boolean;
  /** Related library topic ids (library.ts) and requirement ids (requirements.ts). */
  libraryIds: string[];
  requirementIds: string[];
}

/** The general rule every food-contact surface follows when nothing more specific applies (Food Code Part 4-6). */
export const FOOD_CONTACT_BASICS: string[] = [
  "Scrape or wipe off food.",
  "Wash with hot soapy water.",
  "Rinse with clean water.",
  "Sanitize at the right strength for the right contact time (test with the matching strip).",
  "Let it air-dry. Don't towel it.",
];

// ---------------------------------------------------------------------------------------------------------------
// Shared citations, schedule lines, products and contingencies (each traced to docs/source)
// ---------------------------------------------------------------------------------------------------------------

const SP = (s: string) => `Source protocols: ${s}`;
const SCHED = (row: string) => SP(`master cleaning schedule (${row})`);
const TABLE = (row: string) => SP(`how to sanitize each surface (${row})`);
const SHOP = (row: string) => SP(`shopping list, cleaning products (${row})`);
/** Rows of docs/source/research/upholstery-seating.md (maker care guides, each with its URL and lastChecked). */
const UPH = (rows: string) => `Research: upholstery-seating.md (${rows})`;

type Often = { when: string; citation: string };
const EVERY_4H: Often = { when: "At least every 4 hours while in steady use with TCS food (milk, meat, eggs, cut produce, cooked food)", citation: "Food Code 4-602.11" };
const RAW_SWITCH: Often = { when: "Between raw and ready-to-eat food, between different raw meats, and between raw produce and TCS food", citation: "Food Code 4-602.11" };
const IF_CONTAMINATED: Often = { when: "Any time it may have been contaminated", citation: "Food Code 4-602.11" };
const NON_TCS_EQUIP: Often = { when: "Used only with non-TCS food: as often as the maker says, or often enough to prevent soil and mold", citation: "Food Code 4-602.11(E)" };
const COOK_24H: Often = { when: "Cooking surfaces: at least every 24 hours", citation: "Food Code 4-602.12(A)" };
/** The general rule for a surface food never touches (Food Code 4-601.11(C)); the walk's line for an object with no guide of its own (walkobjects.ts). */
export const NFC_CLEAN: Often = { when: "Keep free of dust, dirt, food residue and other debris", citation: "Food Code 4-601.11(C)" };

// Products (examples exactly as the sources name them)
const P = {
  quat: { productId: "sanitizer_quat", purpose: "Food-contact sanitizer to mix and test", examples: ["Steramine Sanitizing Tablets (quat)", "Ecolab Oasis 146 Multi-Quat", "Kay-5 Sanitizer"], supplyKey: "sanitizer", note: "EPA-registered for food-contact surfaces; use label strength (usually 200–400 ppm)" },
  chlorine: { productId: "sanitizer_chlorine", purpose: "Chlorine sanitizer option", examples: ["Clorox Germicidal Bleach (commercial)", "Plain unscented Clorox bleach with food-contact directions"], supplyKey: "bleach", note: "Mix exactly as the label's food-contact directions say. Clorox Disinfecting Bleach: 2 teaspoons per gallon, 2 minutes, air-dry" },
  spray: { productId: "sanitizer_spray", purpose: "Ready-to-use no-rinse sanitizer spray", examples: ["PURELL Foodservice Surface Sanitizer (EPA 84150-3)", "CloroxPro Clorox Anywhere No Rinse Sanitizer (EPA 67619-44)", "CloroxPro Clorox Anywhere Daily Disinfectant & Sanitizer (EPA 67619-42)"], supplyKey: "sanitizer", note: "No test strip needed; leave wet for the label's time (60 seconds to 1 minute), no rinse" },
  strips: { productId: "test_strips", purpose: "Test strips that match your sanitizer", examples: ["Hydrion QT-40 quat test strips (0–400 ppm)", "Hydrion chlorine test strips (0–200 ppm)", "Hydrion iodine test strips"], supplyKey: "test-strips" },
  dishSoap: { productId: "dish_soap", purpose: "Dish soap for washing by hand", examples: ["Dawn Professional", "Dawn Ultra"] },
  degreaser: { productId: "degreaser", purpose: "Degreaser for grease, ovens and hoods", examples: ["Dawn Powerwash", "Simple Green Pro HD", "Easy-Off Professional (ovens)"], note: "Rinse it off food-contact surfaces before sanitizing" },
  stainless: { productId: "stainless_cleaner", purpose: "Stainless steel cleaner", examples: ["Weiman Stainless Steel Cleaner", "Bar Keepers Friend (scrubbing)"] },
  glass: { productId: "glass_cleaner", purpose: "Glass cleaner (never on food-contact surfaces)", examples: ["Windex Original", "Sprayway Glass Cleaner", "Invisible Glass"] },
  granite: { productId: "stone_cleaner", purpose: "Granite and stone counter cleaner", examples: ["Granite Gold Daily Cleaner", "Weiman Granite & Stone", "Method Daily Granite"], note: "Sanitize with a quat or no-rinse spray labeled safe for stone" },
  cloths: { productId: "wiping_cloths", purpose: "Color-coded microfiber cloths (one color for food areas, one for restrooms)", examples: ["Color-coded microfiber cloths"], supplyKey: "wiping-cloths", note: "Microfiber or quat-compatible cloths keep quat buckets at strength" },
  gloves: { productId: "gloves", purpose: "Nitrile gloves", examples: ["Nitrile or vinyl single-use gloves (not latex)"], supplyKey: "gloves" },
  cafiza: { productId: "espresso_cleaner", purpose: "Espresso backflush and portafilter soak", examples: ["Urnex Cafiza"] },
  dezcal: { productId: "descaler", purpose: "Espresso machine descaler", examples: ["Urnex Dezcal", "The machine maker's descaler"] },
  rinza: { productId: "wand_cleaner", purpose: "Steam wand and milk frother cleaner", examples: ["Urnex Rinza"] },
  grindz: { productId: "grinder_tablets", purpose: "Grinder cleaning", examples: ["Urnex Grindz tablets"] },
  dishwasher: { productId: "dishwasher_detergent", purpose: "Dishwasher detergent", examples: ["Cascade Complete (home machine, sanitize cycle)", "The detergent your commercial machine's service company supplies"] },
  thermometer: { productId: "probe_thermometer", purpose: "Probe thermometer", examples: ["Digital thin-tip probe thermometer"], supplyKey: "thermometer", note: "No glass or mercury thermometers" },
  handSoap: { productId: "hand_soap", purpose: "Hand soap for the handwashing sink (NSF E1)", examples: ["PURELL (GOJO) food-service soap", "Ecolab food-service hand care", "Diversey Soft Care", "Kutol"], supplyKey: "hand-soap" },
  towels: { productId: "paper_towels", purpose: "Single-use paper towels", examples: ["Single-use paper towels"], supplyKey: "paper-towels" },
  bags: { productId: "trash_bags", purpose: "Trash can liners", examples: ["Plastic trash bags"], supplyKey: "trash-bags" },
  restroom: { productId: "toilet_bowl_cleaner", purpose: "Restroom cleaner and disinfectant", examples: ["Clorox Toilet Bowl Cleaner with Bleach", "Lysol Disinfectant Spray", "Scrubbing Bubbles"], supplyKey: "disinfectant" },
  vomit: { productId: "norovirus_disinfectant", purpose: "Vomit and norovirus cleanup", examples: ["Clorox Healthcare Bleach Germicidal Cleaner", "Unscented bleach at 1,000–5,000 ppm"], supplyKey: "bleach", note: "Not quat-only products unless labeled for norovirus" },
  touchWipes: { productId: "disinfectant", purpose: "Disinfectant for touch points (not food-contact surfaces)", examples: ["Clorox Disinfecting Wipes", "Lysol Disinfectant Spray"], supplyKey: "disinfectant" },
  floorTile: { productId: "neutral_floor_cleaner", purpose: "Tile and vinyl floor cleaner", examples: ["Zep Neutral pH Floor Cleaner", "Pine-Sol", "Mr. Clean", "Swiffer WetJet"] },
  floorKitchen: { productId: "degreasing_floor_cleaner", purpose: "Kitchen floor degreaser", examples: ["Zep Heavy-Duty Citrus Degreaser"] },
  kitchenMop: { productId: "kitchen_mop", purpose: "Kitchen mop, bucket and cloths (labeled, kitchen only)", examples: ["A labeled kitchen mop and bucket"], supplyKey: "kitchen-mop" },
  restroomMop: { productId: "restroom_mop", purpose: "Restroom mop, bucket and cloths (labeled, restroom only)", examples: ["A labeled restroom mop and bucket"], supplyKey: "restroom-mop" },
  hardwood: { productId: "hardwood_floor_cleaner", purpose: "Hardwood floor cleaner", examples: ["Bona Hardwood Floor Cleaner"] },
  woodFurniture: { productId: "wood_cleaner", purpose: "Wood table and furniture cleaner", examples: ["Murphy Oil Soap (diluted)", "Pledge Everyday Clean Multisurface", "Method Wood for Good"] },
  upholstery: { productId: "carpet_cleaner", purpose: "Upholstery cleaner (test a hidden spot)", examples: ["Folex Carpet Spot Remover", "Bissell upholstery cleaner", "Resolve upholstery cleaner"] },
  carpet: { productId: "carpet_cleaner", purpose: "Carpet cleaner", examples: ["Resolve", "Bissell"], note: "Professional extraction yearly" },
  alcohol: { productId: "alcohol_swabs", purpose: "70% alcohol swabs for thermometer probes", examples: ["70% alcohol swabs"] },
  nailBrush: { productId: "nail_brush", purpose: "Nail brush for each handwashing sink", examples: ["Nail brush"] },
  toiletPaper: { productId: "toilet_paper", purpose: "Toilet paper", examples: ["Toilet paper"], supplyKey: "toilet-paper" },
  groupBrush: { productId: "group_brush", purpose: "Group-head brush", examples: ["Group-head brush"] },
  waterFilter: { productId: "water_filter_cartridge", purpose: "Water filter cartridge", examples: ["The cartridge your machine's maker names"] },
  ovenCleaner: { productId: "oven_cleaner", purpose: "Oven cleaner (used as its label says)", examples: ["Easy-Off Professional (ovens)"] },
  grillBrush: { productId: "grill_brush", purpose: "Grill brush", examples: ["Grill brush"] },
  fridgeThermo: { productId: "fridge_thermometer", purpose: "Fridge thermometer", examples: ["Refrigerator thermometer"] },
  wetFloorSign: { productId: "wet_floor_sign", purpose: "Wet floor sign", examples: ["Wet floor sign"] },
} satisfies Record<string, ProductPick>;

const SANITIZE_SET: ProductPick[] = [P.quat, P.strips, P.spray];

// Contingencies used by many surfaces
const C_WEAK: Contingency = {
  if: "Sanitizer tested weak",
  then: [
    "Add sanitizer and test again with the matching strip.",
    "If the water is dirty or cloudy, dump it and mix fresh. Never just add more to a dirty bucket.",
    "Too strong? Add water and retest. Stronger is not safer.",
    "Log the test.",
  ],
  citations: ["Food Code 4-302.14", "Food Code 4-501.114", SP("testing sanitizer strength"), SP("wiping cloths (keep the cloth bucket strong enough to work)")],
};
const C_OUT_SANITIZER: Contingency = {
  if: "Out of the recommended sanitizer",
  then: [
    "Use plain, unscented bleach whose label has food-contact directions, mixed exactly as that label says. Clorox Disinfecting Bleach: 2 teaspoons per gallon.",
    "Test with a chlorine strip. Keep the surface wet for the label's time, 2 minutes for Clorox bleach, then air-dry.",
    "Small items can instead go fully under hot water at 171°F or hotter for 30 seconds (check with a thermometer).",
    "Never use scented or splashless bleach, household disinfectant wipes, glass cleaner or floor cleaner on food-contact surfaces.",
  ],
  citations: [SP("mixing a 50 ppm chlorine sanitizing sink"), "Food Code 4-703.11", "Food Code 7-204.11", SP("legal criteria for sanitizers (in practice)")],
};
const C_VOMIT: Contingency = {
  if: "Someone vomited nearby (or had diarrhea)",
  then: [
    "Stop food service nearby. Cover and throw out any open food within about 25 feet.",
    "Keep people away. Put on disposable gloves, a mask and an apron.",
    "Cover the mess with paper towels or absorbent powder and scoop it into a plastic bag.",
    "Clean the area with detergent and water.",
    "Disinfect with an EPA-registered norovirus disinfectant, or bleach at 1,000–5,000 ppm (about 1/3 to 1 1/3 cups of 6% bleach per gallon), for the label contact time (usually 5 minutes).",
    "Bag everything, including gloves and apron. Tie it off and throw it out.",
    "Wash hands well. Launder any cloth items in hot water.",
    "Log the time, the place and what was thrown out.",
  ],
  citations: ["Food Code 2-501.11", SP("vomit or diarrhea cleanup")],
};
const C_RAW_MEAT: Contingency = {
  if: "Raw meat, poultry, fish or eggs touched it",
  then: [
    "Scrape, wash, rinse and sanitize it (full contact time, air-dry) before any produce or ready-to-eat food touches it.",
    "Change gloves and wash hands before the next task.",
    "Or switch to a fresh, clean one and send this one to the dish sink.",
  ],
  citations: ["Food Code 4-602.11", "Food Code 3-302.11(A)(2)", SP("prep order: timing to prevent cross-contamination")],
};
const C_DROPPED: Contingency = {
  if: "It was dropped",
  then: ["Don't use it again until it is washed, rinsed and sanitized.", "Grab a clean one to keep working."],
  citations: [TABLE("Knives, tongs, spatulas, serving utensils: when dropped"), SCHED("Tongs and scoops: when dropped")],
};
const C_ALLERGEN: Contingency = {
  if: "An allergen touched it (nuts, milk, shellfish, wheat, sesame)",
  then: [
    "Wash it with soap and water, rinse, then sanitize. Sanitizer alone does not remove allergens.",
    "Or use a separate, clean one for the allergen-free order.",
    "Change gloves and wash hands after handling the allergen food.",
  ],
  citations: [SP("cross-contact (does sanitizer fix it? No; wash with soap and water)"), "Food Code 3-602.12(C)"],
};
const C_FOUR_HOURS: Contingency = {
  if: "It's been in use for 4 hours",
  then: ["Take it out of service and wash, rinse, sanitize and air-dry it.", "Swap in a clean one so work can continue."],
  citations: ["Food Code 4-602.11"],
};
const C_PESTS: Contingency = {
  if: "Droppings, insects or other pest signs on or near it",
  then: [
    "Stop using the affected food and supplies. Throw out anything contaminated.",
    "Clean and sanitize the area.",
    "Call your licensed pest control operator the same day for rodents or roaches. Don't spray pesticides yourself.",
    "Log what you saw and what you did.",
  ],
  citations: ["Food Code 6-501.111", "Food Code 6-501.112", SP("if you find pests"), SP("action thresholds")],
};
const C_WORN: Contingency = {
  if: "It's cracked, chipped, deeply scratched, pitted or warped",
  then: ["Take it out of service and replace it. Deep scratches and cracks trap food and can't be cleaned to sight and touch."],
  citations: ["Food Code 4-101.11", "Food Code 4-601.11"],
};
const C_WATER_OUT: Contingency = {
  if: "Water is shut off, discolored, or there's a boil-water notice",
  then: ["Stop making drinks and ice until the water is cleared.", "Report backflow devices that leak, spit or drip, and stop using that water until checked."],
  citations: [SP("backflow protection (if building water is shut off, discolored or a boil-water notice is issued)"), SP("backflow: everyday practices")],
};

const L = {
  clean: "cleaning-sanitizing", sink3: "three-compartment-sink", dish: "dishwashers", cloths: "wiping-cloths", vomit: "vomit-diarrhea-cleanup",
  chem: "chemicals-sds", restroom: "restroom-mops-rags", pests: "pest-prevention", pestSigns: "pest-signs", thermo: "thermometers",
  crossContact: "cross-contact", allergens: "allergens", fridge: "fridge-storage-order", dry: "dry-storage-fifo", hands: "handwashing",
  gloves: "gloves-bare-hands", buffet: "buffets-self-service", holding: "hot-cold-holding", cooking: "cooking-temperatures",
} as const;

const R = {
  sanitize: "fc-sanitizing", freq: "fc-cleaning-frequency", allergens: "fc-allergens", storage: "fc-storage", thermo: "fc-thermometers",
  pests: "fc-pest-control", toilet: "fc-toilet-rooms", tools: "fc-cleaning-tools", chem: "fc-chemicals", vomit: "fc-vomit-cleanup",
  backflow: "fc-backflow", hands: "fc-handwashing", bare: "fc-bare-hand", holding: "fc-holding", self: "fc-self-service", produce: "fc-produce-washing",
} as const;

const WRS = "Wash, rinse and sanitize it, then let it air-dry.";

// ---------------------------------------------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------------------------------------------

/** The catalog with a state's words (its citations and notes). */
function buildSurfaces(W: StateWords<typeof SURFACES_UT>): SurfaceGuide[] {
return [
  // ------------------------------------------------------------ COFFEE BAR
  {
    id: "espresso-group-heads", name: "Espresso group heads, portafilters and baskets", area: "coffee_bar", kind: "food_contact", materials: ["stainless steel", "brass"],
    howOften: [
      { when: "Backflush with plain water after each rush (or shift)", citation: SCHED("Portafilters, baskets, group heads") },
      { when: "Backflush with espresso-machine detergent and soak baskets and group screens daily, at close", citation: SP("safe food handling (espresso machine)") },
    ],
    steps: [
      "Knock out the grounds into the knock box.",
      "Backflush the group with plain water after each rush.",
      "At close, backflush with espresso-machine detergent as the label says, then backflush with plain water to rinse.",
      "Soak the baskets, portafilters and group screens in espresso detergent (a detergent, not sanitizer).",
      "Scrub the group gasket area with a group-head brush.",
      "Rinse well and let everything air-dry.",
    ],
    products: [P.cafiza, P.groupBrush, P.gloves, P.stainless],
    contingencies: [
      { if: "Out of espresso detergent", then: ["Wash baskets and portafilters by hand in hot soapy water (a detergent for coffee oils), rinse and air-dry.", "Backflush with plain water only until you restock.", "Follow the machine maker's instructions."], citations: ["Food Code 4-603.14", SCHED("Portafilters, baskets, group heads")] },
      { if: "Detergent got on your gloves or hands during backflushing", then: ["Change gloves and wash hands before making drinks."], citations: [SP("changing gloves (cleaning the steam wand, backflushing with detergent)")] },
      C_DROPPED,
    ],
    dont: ["Don't soak baskets in sanitizer; use espresso detergent.", "Never wash hands in the drip tray or rinse cup.", "Don't skip the plain-water rinse after a detergent backflush."],
    citations: [SCHED("Portafilters, baskets, group heads"), SP("safe food handling (espresso machine)"), "Food Code 4-603.14", SHOP("Espresso backflush and portafilter soak")],
    keywords: ["espresso", "espresso machine", "group head", "portafilter", "basket", "backflush", "shower screen", "coffee"],
    libraryIds: [L.clean], requirementIds: [R.sanitize, R.freq],
  },
  {
    id: "steam-wand", name: "Steam wand", area: "coffee_bar", kind: "food_contact", materials: ["stainless steel"],
    howOften: [
      { when: "Purge before and after every drink and wipe with the wand-only cloth", citation: SCHED("Steam wand") },
      { when: "Soak the tip in wand cleaner at close", citation: SCHED("Steam wand") },
    ],
    steps: [
      "Purge the wand (a short blast of steam) before steaming.",
      "Right after steaming, wipe the wand with a clean, damp cloth used only for the wand.",
      "Purge again.",
      "At close, soak the tip in steam wand cleaner as the label says.",
      "Rinse it and purge before the next use.",
    ],
    products: [P.rinza, P.cloths],
    contingencies: [
      { if: "Switching milk types (dairy, oat, almond, soy)", then: ["Purge and wipe the wand between milk types.", "Use a separate, labeled pitcher for each milk type.", "For a severe allergy, use a fresh pitcher and a fully cleaned wand."], citations: [SP("cross-contact, prevention (purge and wipe the steam wand between milk types)")] },
      { if: "The wand cloth is used up or dirty", then: ["Swap in a fresh, clean damp cloth. Use it for the wand and nothing else."], citations: [SP("wiping cloths and laundry (steam-wand cloth)")] },
      { if: "Milk is crusted on the wand", then: ["Soak the tip in steam wand cleaner (an acid cleaner for milkstone), rinse and purge."], citations: ["Food Code 4-603.14"] },
    ],
    dont: ["Never use the wand cloth on counters or anything else.", "Never let a customer's cup touch the wand.", "Never re-steam leftover milk; discard it."],
    citations: [SCHED("Steam wand"), SP("wiping cloths and laundry (steam-wand cloth)"), SP("flow of food (steaming milk)"), SHOP("Steam wand and milk frother cleaner")],
    keywords: ["steam wand", "milk frother", "frother", "steamer", "wand", "latte", "milk"],
    libraryIds: [L.clean, L.crossContact], requirementIds: [R.sanitize, R.allergens],
  },
  {
    id: "espresso-descale", name: "Espresso machine descaling and water filter", area: "coffee_bar", kind: "equipment",
    howOften: [
      { when: W.descaleWhen ?? "Descale as the maker says", citation: SCHED("Espresso machine descale") },
      { when: "Replace the water filter cartridge as the maker says (often every 3–6 months)", citation: SCHED("Water filter") },
      { when: "Yearly service by an espresso technician", citation: SCHED("Espresso machine descale (contractor)") },
    ],
    steps: [
      "Put on gloves.",
      "Mix and run the descaler exactly as the machine maker's instructions say.",
      "Flush with plenty of clean water to rinse out all descaler.",
      "Replace the water filter cartridge on the maker's schedule.",
      "Write the date on the cleaning sign-off log.",
    ],
    products: [P.dezcal, P.waterFilter, P.gloves],
    contingencies: [
      { if: "Descaler splashed in eyes or on skin", then: ["Rinse with water for 15 minutes.", "Follow the first-aid section of the product's safety data sheet.", "Call Poison Control: 1-800-222-1222."], citations: [SP("safe chemical use")] },
      { if: "The machine is plumbed into the water line", then: ["Have a licensed plumber confirm a dual check valve with an intermediate vent (ASSE 1022) is installed ahead of it."], citations: one(W.backflowCite) },
      C_WATER_OUT,
    ],
    dont: ["Never mix descaler with bleach or other chemicals.", "Don't store descaler above food, cups or the machine."],
    citations: [SCHED("Espresso machine descale"), SCHED("Water filter"), "Food Code 4-603.14", "Food Code 7-201.11"],
    keywords: ["descale", "scale", "limescale", "hard water", "water filter", "espresso machine", "coffee machine"],
    libraryIds: [L.chem], requirementIds: [R.backflow, R.chem],
  },
  {
    id: "espresso-exterior", name: "Espresso machine outside, drip tray and knock box", area: "coffee_bar", kind: "non_food_contact", materials: ["stainless steel"],
    howOften: [
      { when: "Wipe drips on the outside during service", citation: SP("what wiping cloths are for") },
      { when: "Empty the drip tray and knock box every day", citation: SP("integrated pest management (prevention)") },
    ],
    steps: [
      "Wipe drips on the outside with a wiping cloth from the sanitizer bucket.",
      "Empty the knock box into the trash or compost.",
      "Empty and wash the drip tray.",
      "Polish the stainless outside with a stainless steel cleaner.",
    ],
    products: [P.stainless, P.cloths],
    contingencies: [
      { if: "Ants trailing to the drip tray", then: ["Empty and wash the drip tray and knock box.", "Act within 24 hours; clean and remove what attracted them."], citations: [SP("weekly checklist (no ant trails to the machine drip tray)"), SP("action thresholds")] },
      C_PESTS,
    ],
    dont: ["Never pour coffee grounds down the drain.", "Don't use steel wool on stainless.", "Never wash hands in the drip tray."],
    citations: [SP("integrated pest management (prevention)"), SP("solid waste (coffee grounds never down the drain)"), SHOP("Stainless steel")],
    keywords: ["espresso machine", "drip tray", "knock box", "machine outside", "stainless", "coffee machine"],
    libraryIds: [L.cloths, L.pests], requirementIds: [R.pests],
  },
  {
    id: "coffee-grinder", name: "Coffee grinder hopper and burrs", area: "coffee_bar", kind: "food_contact",
    howOften: [{ when: "Weekly", citation: SCHED("Grinder hopper and burrs") }],
    steps: [
      "Empty the beans from the hopper.",
      "Brush out the burrs and the chute.",
      "Run grinder cleaning tablets as the label says, if you use them.",
      "Wash the hopper with soap and water.",
      "Dry it completely before refilling.",
    ],
    products: [P.grindz, P.dishSoap],
    contingencies: [
      { if: "Insects or webbing in the beans", then: ["Throw the beans out.", "Clean the hopper and the storage bin.", "Act within 24 hours on pantry insects."], citations: [SP("weekly checklist (no insects, webbing or larvae in beans)"), SP("action thresholds")] },
    ],
    dont: ["Don't put the hopper back wet.", "Don't store beans on the floor."],
    citations: [SCHED("Grinder hopper and burrs"), SHOP("Grinder cleaning")],
    keywords: ["grinder", "coffee grinder", "burrs", "hopper", "beans"],
    libraryIds: [L.clean], requirementIds: [R.freq],
  },
  {
    id: "milk-pitchers", name: "Milk pitchers", area: "coffee_bar", kind: "food_contact", materials: ["stainless steel"],
    howOften: [
      { when: "Between milk types, every 4 hours, and at close", citation: SCHED("Milk pitchers") },
      EVERY_4H,
    ],
    steps: [
      "Pour out leftover steamed milk. Never pour it back into the carton.",
      "Wash with a pitcher brush in hot soapy water.",
      "Rinse with clean water.",
      "Sanitize for the full contact time.",
      "Air-dry upside down.",
    ],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "Switching to a different milk (almond is a tree nut)", then: ["Use the separate, labeled pitcher for that milk.", "Wash the used pitcher before it's used for any other milk."], citations: [SP("cross-contact, prevention"), SP("flow of food (holding: pitchers washed between milk types)")] },
      C_WEAK, C_OUT_SANITIZER,
    ],
    dont: ["Never re-steam milk.", "Don't share one pitcher between dairy and alt-milks.", "Don't towel-dry."],
    citations: [SCHED("Milk pitchers"), "Food Code 4-602.11", SP("cross-contact")],
    keywords: ["milk pitcher", "pitcher", "jug", "steaming pitcher", "milk", "latte"],
    libraryIds: [L.clean, L.crossContact], requirementIds: [R.sanitize, R.freq, R.allergens],
  },
  {
    id: "drip-brewer", name: "Drip coffee brewer and coffee urn", area: "coffee_bar", kind: "equipment",
    howOften: [
      { when: "Brew basket and filters: wash, rinse and sanitize after each batch", citation: SP("flow of food (brewing: brewer and filters after each batch)") },
      NON_TCS_EQUIP,
      { when: "Delime (remove scale) as the maker says", citation: "Food Code 4-603.14" },
    ],
    steps: [
      "Throw out used grounds and filters.",
      "Wash the brew basket and carafe or urn in hot soapy water, rinse and sanitize.",
      "Air-dry.",
      "Run delimer (an acid cleaner) through the brewer or urn as the maker says, then flush with clean water.",
      "Wipe the outside.",
    ],
    products: [P.dishSoap, ...SANITIZE_SET, P.dezcal, P.stainless],
    contingencies: [
      { if: "A customer wants a refill in their own cup", then: ["Refuse cups that are visibly dirty.", "Don't let the cup touch the brewer nozzle or urn spigot; pour from above.", "Wash hands after handling customer cups."], citations: ["Food Code 3-304.17(B)", SP("refilling customers' own cups")] },
      { if: "The brewer or urn is plumbed into the water line", then: ["Have a plumber confirm a dual check valve with an intermediate vent (ASSE 1022) ahead of it."], citations: one(W.backflowCite) },
      C_WATER_OUT,
    ],
    dont: ["Don't pour grounds down the drain.", "Don't skip the rinse after delimer."],
    citations: ["Food Code 4-602.11(E)", "Food Code 4-603.14", ...one(W.backflowCite)],
    keywords: ["coffee maker", "drip coffee", "brewer", "urn", "coffee urn", "carafe", "airpot"],
    libraryIds: [L.clean], requirementIds: [R.freq, R.backflow],
  },
  {
    id: "cold-brew-containers", name: "Cold brew containers and filters", area: "coffee_bar", kind: "food_contact",
    howOften: [{ when: "Every batch", citation: SCHED("Cold brew containers and filters") }],
    steps: ["Empty and scrape out the grounds.", ...FOOD_CONTACT_BASICS.slice(1)],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "Cold brew was steeped on the counter", then: ["Throw it out.", "Steep only in the fridge at 41°F or below."], citations: [SP("corrective actions (cold brew steeped on the counter: discard)")] },
      C_WEAK,
    ],
    dont: ["Don't use shared household pitchers for cold brew.", "Don't steep at room temperature."],
    citations: [SCHED("Cold brew containers and filters"), SP("flow of food (brewing and cold brew steeping)")],
    keywords: ["cold brew", "cold brew container", "toddy", "filter bag", "iced coffee"],
    libraryIds: [L.clean, L.holding], requirementIds: [R.sanitize, R.holding],
  },
  {
    id: "syrup-pumps", name: "Syrup pumps and bottles", area: "coffee_bar", kind: "food_contact",
    howOften: [{ when: "Weekly and when changing bottles", citation: SCHED("Syrup pumps") }],
    steps: ["Pump hot water through the pump until it runs clear.", "Take the pump apart and wash it in hot soapy water.", "Rinse and sanitize.", "Air-dry, then put it on the new bottle.", "Label the pump with the flavor, allergens and date opened."],
    products: [P.dishSoap, ...SANITIZE_SET],
    contingencies: [{ if: "The syrup has real nuts (almond, hazelnut)", then: ["Label the pump with the allergen.", "Keep that pump on its own bottle."], citations: [SP("syrups (label each syrup pump with its flavor, allergens and date opened)")] }],
    dont: ["Don't move a nut-syrup pump to another bottle without washing it."],
    citations: [SCHED("Syrup pumps"), SP("syrups")],
    keywords: ["syrup", "syrup pump", "pump", "flavor", "sauce bottle"],
    libraryIds: [L.clean, L.allergens], requirementIds: [R.freq, R.allergens],
  },

  // ------------------------------------------------------------ BAR AND BEVERAGE
  {
    id: "blender-jars", name: "Blender jars", area: "bar_beverage", kind: "food_contact",
    howOften: [
      { when: "Between nut and nut-free drinks, and every 4 hours", citation: SCHED("Blender jars") },
      EVERY_4H,
    ],
    steps: FOOD_CONTACT_BASICS,
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "The last drink had nut milk or nut syrup", then: ["Wash the jar with soap and water, rinse and sanitize before the next drink.", "A quick rinse is not enough; soap removes allergens."], citations: [SP("cross-contact, prevention (wash blenders between drinks with nut milks or syrups)")] },
      C_ALLERGEN, C_WEAK,
    ],
    dont: ["Don't just rinse between allergen and allergen-free drinks."],
    citations: [SCHED("Blender jars"), SP("cross-contact")],
    keywords: ["blender", "smoothie", "frappe", "blender jar", "shake"],
    libraryIds: [L.clean, L.crossContact], requirementIds: [R.sanitize, R.allergens],
  },
  {
    id: "ice-machine", name: "Ice machine and ice bin", area: "bar_beverage", kind: "food_contact",
    howOften: [
      { when: "Ice bin: sanitize monthly", citation: SCHED("Ice machine and bin") },
      { when: "Professional service yearly", citation: SCHED("Ice machine and bin (contractor)") },
    ],
    steps: [
      "Turn the machine off and empty the ice as the maker's instructions say.",
      "Wash the bin and ice-contact parts, rinse and sanitize per the maker.",
      "Let it air-dry, then restart.",
      "Keep the scoop outside the ice, in a clean holder.",
      "Log it on the cleaning sign-off log.",
    ],
    products: [...SANITIZE_SET],
    contingencies: [
      C_WATER_OUT,
      { if: "Someone scooped ice with a glass or cup", then: ["Stop them and hand them the scoop with a handle.", "Ice is ready-to-eat food: gloves or scoop only, never hands."], citations: [SP("utensils for taking food (ice: scoop with a handle; never the cup)"), SP("changing gloves (scooping ice)")] },
      { if: "The drain is connected straight to the sewer", then: ["Have a plumber give it an air gap (indirect drain)."], citations: one(W.airGapCite) },
    ],
    dont: ["Never store the scoop in the ice.", "Never scoop with a glass.", "Keep drink ice separate from ice used to cool food or cans."],
    citations: [SCHED("Ice machine and bin"), TABLE("Ice scoops and ice bins"), SP("facility requirements (ice)"), ...one(W.airGapCite)],
    keywords: ["ice machine", "ice maker", "ice bin", "ice"],
    libraryIds: [L.clean, L.gloves], requirementIds: [R.sanitize, R.backflow],
  },
  {
    id: "ice-scoop", name: "Ice scoops", area: "bar_beverage", kind: "food_contact",
    howOften: [
      { when: "Daily", citation: TABLE("Ice scoops and ice bins") },
      { when: "Every 4 hours and when dropped", citation: SCHED("Tongs and scoops") },
    ],
    steps: [WRS, "Store it outside the ice in a clean holder, handle up.", "Wash hands (or change gloves) before scooping ice."],
    products: [...SANITIZE_SET, P.gloves],
    contingencies: [
      { if: "The scoop was left in the ice", then: ["Wash, rinse and sanitize the scoop.", "Store it outside the ice from now on."], citations: [TABLE("Ice scoops and ice bins: store scoop outside ice")] },
      C_DROPPED,
    ],
    dont: ["Never use a glass or cup as a scoop.", "Never touch ice with bare hands."],
    citations: [TABLE("Ice scoops and ice bins"), SCHED("Tongs and scoops"), SP("utensils for taking food (ice)")],
    keywords: ["ice scoop", "scoop", "ice"],
    libraryIds: [L.clean, L.gloves], requirementIds: [R.sanitize, R.bare],
  },
  {
    id: "beverage-nozzles", name: "Soda and drink dispenser nozzles", area: "bar_beverage", kind: "food_contact",
    howOften: [
      { when: "Iced tea dispensers: at least every 24 hours", citation: "Food Code 4-602.11(E)" },
      NON_TCS_EQUIP,
    ],
    steps: [
      "Take off the nozzles and diffusers as the maker's instructions say.",
      ...FOOD_CONTACT_BASICS.slice(1),
      "Put them back and wipe the dispenser face.",
    ],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "A customer wants a refill in their own cup", then: ["Don't let the cup touch the nozzle, spout or tap.", "Refuse visibly dirty cups."], citations: ["Food Code 3-304.17(B)"] },
      { if: "Mold or slime on a nozzle", then: ["Take it off and wash, rinse and sanitize it now.", "Clean more often: often enough to prevent soil and mold."], citations: ["Food Code 4-602.11(E)"] },
      C_WATER_OUT,
    ],
    dont: ["Don't let cups touch the nozzle."],
    citations: ["Food Code 4-602.11(E)", ...one(W.backflowCite), ...one(W.airGapCite)],
    keywords: ["soda", "soda fountain", "nozzle", "fountain drink", "iced tea", "beverage dispenser", "drink dispenser", "lemonade"],
    libraryIds: [L.clean], requirementIds: [R.freq, R.backflow],
  },

  // ------------------------------------------------------------ PREP
  {
    id: "prep-counter", name: "Food prep counters and tables", area: "prep", kind: "food_contact", materials: ["stainless steel", "plastic", "granite", "stone"],
    howOften: [
      { when: "Before opening (before prep), between tasks, every 4 hours, and at close", citation: SCHED("Counters and prep area") },
      RAW_SWITCH, EVERY_4H,
    ],
    steps: [
      "Clear the counter and scrape off food.",
      "Wash with hot soapy water.",
      "Rinse with clean water.",
      "Spray or wipe with sanitizer and leave it wet for the full contact time.",
      "Let it air-dry.",
      "Plan your order: produce and ready-to-eat food first, raw meat last.",
    ],
    products: [...SANITIZE_SET, P.dishSoap, P.stainless, P.granite],
    contingencies: [
      { if: "Raw meat or raw chicken was on the counter", then: [
        "Scrape, wash, rinse and sanitize the counter, board and knife.",
        "Change gloves and wash hands.",
        "Only then go back to produce or ready-to-eat food.",
        "Next time: produce and ready-to-eat first, then raw meats lowest cooking temperature first (fish, then whole beef, pork and lamb, then ground meat, then poultry last).",
      ], citations: [SP("prep order: timing to prevent cross-contamination"), "Food Code 4-602.11", "Food Code 3-302.11(A)(2)"] },
      { if: "Switching between different raw meats (beef, poultry, fish)", then: ["Wash, rinse and sanitize the surface and tools between types.", "Change gloves and wash hands.", "Or use separate prep tables or areas for each type."], citations: ["Food Code 3-302.11(A)(2)", "Food Code 4-602.11"] },
      { if: "Cutting vegetables or fruit", then: ["Wash produce before cutting.", "Use a clean, sanitized counter and board; if raw meat was just there, wash, rinse and sanitize first."], citations: ["Food Code 3-302.15", SP("prep order")] },
      { if: "The counter is granite or stone", then: ["Clean with a stone cleaner.", "Sanitize with a quat or no-rinse spray labeled safe for stone.", "No vinegar, lemon, bleach or abrasive powders."], citations: [SHOP("Granite and stone counters")] },
      C_WEAK, C_OUT_SANITIZER, C_VOMIT,
    ],
    dont: [
      "A swipe with a bucket cloth doesn't sanitize a prep surface; do the full wash, rinse, sanitize.",
      "Never use disinfectant sprays, glass cleaner, polish or floor cleaner on a food counter.",
      "Don't put raw meat and ready-to-eat food on the same counter at the same time.",
    ],
    citations: [SCHED("Counters and prep area"), TABLE("Prep tables and counters"), "Food Code 4-602.11", SP("what wiping cloths are for"), SP("prep order")],
    keywords: ["counter", "countertop", "prep table", "prep counter", "work table", "raw chicken", "raw meat", "vegetables", "produce", "meat counter", "butcher counter"],
    libraryIds: [L.clean, L.cloths, L.crossContact], requirementIds: [R.sanitize, R.freq, R.storage],
  },
  {
    id: "cutting-board-plastic", name: "Plastic cutting boards (color-coded)", area: "prep", kind: "food_contact", materials: ["plastic"],
    howOften: [
      { when: "Between raw and ready-to-eat, every 4 hours, and after use", citation: TABLE("Cutting boards") },
      RAW_SWITCH,
      { when: "After any interruption", citation: SP("prep order (clean and sanitize boards after any interruption)") },
    ],
    steps: [
      "Scrape off food.",
      "Wash in hot soapy water (three-compartment sink or dishwasher).",
      "Rinse.",
      "Sanitize by dipping or spraying for the full contact time.",
      "Air-dry.",
      "Use the right color: red for beef, pork and lamb; yellow for poultry; blue for fish; green for produce; white for ready-to-eat.",
    ],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      C_RAW_MEAT,
      { if: "No free board in the right color", then: ["Wash, rinse and sanitize a board fully (contact time, air-dry) before switching foods.", "On a shared board, cut produce and ready-to-eat food first and raw meat last."], citations: [SP("prep order")] },
      { if: "The board has deep cuts or grooves", then: ["Replace it. Deeply scored boards can't be cleaned."], citations: ["Food Code 4-101.11(E)"] },
      C_ALLERGEN, C_WEAK,
    ],
    dont: ["Don't use a raw-meat board for produce without washing, rinsing and sanitizing.", "Don't towel-dry.", "Never put cooked food on a board that held raw meat."],
    citations: [TABLE("Cutting boards"), SP("separate raw meat types (color-coded boards)"), "Food Code 4-602.11", "Food Code 4-101.11"],
    keywords: ["cutting board", "chopping board", "board", "color coded", "red board", "green board", "plastic board"],
    libraryIds: [L.clean, L.sink3, L.crossContact], requirementIds: [R.sanitize, R.freq, R.storage],
  },
  {
    id: "cutting-board-wood", name: "Wood cutting boards and butcher blocks", area: "prep", kind: "food_contact", materials: ["wood", "bamboo"],
    howOften: [
      { when: "Between raw and ready-to-eat, every 4 hours, and after use (same rule as every cutting board)", citation: TABLE("Cutting boards") },
      RAW_SWITCH,
    ],
    steps: [
      "Scrape off food.",
      "Wash with hot soapy water.",
      "Rinse with clean water.",
      "Sanitize by spraying or wiping both sides for the full contact time. Never dip or soak it.",
      "Let it air-dry, standing on its edge.",
      "Oil it with food-grade mineral oil when it looks dry.",
    ],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "The wood board has deep cuts, cracks or splits", then: ["Replace it. Surfaces must stay smooth and free of cracks so they can be cleaned."], citations: ["Food Code 4-101.11(E)", SP("facility requirements (equipment and food-contact surfaces: free of cracks)")] },
      C_RAW_MEAT,
      { if: "Is wood allowed?", then: ["Yes. Wood is allowed for cutting boards and butcher blocks (bare wood otherwise is not allowed on food-contact surfaces).", "It still needs the full wash, rinse, sanitize, air-dry."], citations: [SP("facility requirements (no bare wood except cutting boards, butcher blocks, bakers' tables)")] },
      C_ALLERGEN, C_WEAK,
    ],
    dont: ["Don't keep using a board with deep cuts or cracks.", "Don't towel-dry.", "Don't treat a wipe with the bucket cloth as sanitizing."],
    citations: [TABLE("Cutting boards"), SP("facility requirements (equipment: no bare wood except cutting boards and butcher blocks)"), "Food Code 4-101.11", "Food Code 4-602.11"],
    keywords: ["wood cutting board", "wooden board", "butcher block", "bamboo board", "wood board", "cutting board", "chopping block"],
    libraryIds: [L.clean, L.crossContact], requirementIds: [R.sanitize, R.freq],
  },
  {
    id: "knives-utensils", name: "Knives, spatulas and kitchen utensils", area: "prep", kind: "food_contact", materials: ["stainless steel", "plastic"],
    howOften: [
      { when: "Between raw and cooked, every 4 hours in continuous use, and when dropped", citation: TABLE("Knives, tongs, spatulas, serving utensils") },
      { when: "After each use", citation: SCHED("Cutting boards, knives, utensils") },
    ],
    steps: ["Scrape off food.", "Wash in sink 1 (110°F or hotter).", "Rinse in sink 2.", "Sanitize in sink 3 for the full contact time.", "Air-dry, then store handles up."],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [C_RAW_MEAT, C_DROPPED, C_FOUR_HOURS, C_ALLERGEN, { if: "A spatula is melted or a utensil is warped", then: ["Replace it."], citations: ["Food Code 4-101.11(E)"] }],
    dont: ["Don't use a raw-meat knife on produce without a full wash, rinse, sanitize.", "Don't store utensils eating-end up."],
    citations: [TABLE("Knives, tongs, spatulas, serving utensils"), SCHED("Cutting boards, knives, utensils"), "Food Code 4-602.11"],
    keywords: ["knife", "knives", "spatula", "utensils", "ladle", "whisk", "spoon", "peeler"],
    libraryIds: [L.clean, L.sink3], requirementIds: [R.sanitize, R.freq],
  },
  {
    id: "tongs", name: "Tongs and serving utensils", area: "prep", kind: "food_contact",
    howOften: [
      { when: "Every 4 hours, after nut pastries, and when dropped", citation: SCHED("Tongs and scoops") },
      { when: "Self-service tongs for non-TCS food: at least every 24 hours", citation: "Food Code 4-602.11(E)" },
    ],
    steps: [WRS, "Store tongs on a clean, sanitized plate, not the bare counter.", "Keep raw-meat tongs separate from cooked-food tongs."],
    products: [...SANITIZE_SET],
    contingencies: [
      { if: "Tongs touched raw meat", then: ["Don't use them on cooked or ready-to-eat food.", "Use separate tongs for cooked food; wash, rinse and sanitize the raw tongs."], citations: [SP("barbecue (raw meat has its own tongs)"), SP("utensils for taking food (burgers: tongs separate from raw-meat tongs)")] },
      { if: "They touched a nut pastry", then: ["Wash and sanitize before any nut-free item, or use dedicated nut tongs."], citations: [SP("utensils for taking food (dedicated tongs for nut pastries)")] },
      { if: "A guest touched the food end, or they went in another dish", then: ["Swap them for clean ones."], citations: [SP("utensils (change when dropped, used in another dish, or touched by a guest's hand)")] },
      C_DROPPED,
    ],
    dont: ["Don't set tongs on the bare table or a used plate.", "Never share tongs between raw and cooked meat."],
    citations: [SCHED("Tongs and scoops"), "Food Code 3-304.12", "Food Code 4-602.11"],
    keywords: ["tongs", "serving spoon", "serving utensil", "scoop", "ladle"],
    libraryIds: [L.clean, L.buffet, L.crossContact], requirementIds: [R.sanitize, R.freq, R.self],
  },
  {
    id: "thermometer-probe", name: "Thermometer probes", area: "prep", kind: "food_contact",
    howOften: [
      { when: "Between every food item", citation: SCHED("Thermometer") },
      { when: "Before using or storing it", citation: "Food Code 4-602.11" },
      { when: "Calibrate daily (ice point)", citation: SCHED("Thermometer") },
    ],
    steps: ["Wash the probe.", "Rinse it.", "Sanitize with a 70% alcohol swab or sanitizer dip.", "Let it air-dry.", "Store it in its sleeve."],
    products: [P.thermometer, P.alcohol],
    contingencies: [
      { if: "It was dropped, or a reading seems wrong", then: ["Check it in ice water: crushed ice plus water, probe in the center, wait 30 seconds; it should read 32°F.", "Adjust it per the maker, or replace it.", "Log the calibration."], citations: [SP("calibrating thermometers"), "Food Code 4-203.11"] },
      { if: "It went from raw meat to ready-to-eat food", then: ["Wash and sanitize the probe before the next item."], citations: [SP("sanitizing thermometers")] },
    ],
    dont: [...one(W.boilingCheckDont), "Don't submerge a digital body unless it is waterproof.", "No glass or mercury thermometers."],
    citations: [SCHED("Thermometer"), SP("sanitizing thermometers"), SP("calibrating thermometers"), "Food Code 4-602.11"],
    keywords: ["thermometer", "probe", "temperature probe", "calibrate"],
    libraryIds: [L.thermo], requirementIds: [R.thermo],
  },
  {
    id: "slicer", name: "Meat and deli slicer", area: "prep", kind: "equipment", materials: ["stainless steel"],
    howOften: [EVERY_4H, RAW_SWITCH, IF_CONTAMINATED],
    steps: [
      "Take it apart for cleaning only as the maker's instructions say.",
      ...FOOD_CONTACT_BASICS,
      "Put it back together once dry.",
    ],
    products: [...SANITIZE_SET, P.dishSoap, P.gloves],
    contingencies: [
      { if: "You only wiped it with the bucket cloth", then: ["That doesn't count. Do the full scrape, wash, rinse, sanitize, air-dry."], citations: [SP("what wiping cloths are for (slicers need the full sequence)")] },
      C_RAW_MEAT, C_FOUR_HOURS, C_ALLERGEN,
    ],
    dont: ["Don't clean it with a wiping cloth alone."],
    citations: ["Food Code 4-602.11", "Food Code 4-603.14", SP("what wiping cloths are for"), SP("facility requirements (lighting: 50 foot-candles where slicers are used)")],
    keywords: ["slicer", "deli slicer", "meat slicer", "cheese slicer", "deli"],
    libraryIds: [L.clean, L.cloths], requirementIds: [R.sanitize, R.freq],
  },
  {
    id: "mixer", name: "Stand mixer and attachments", area: "prep", kind: "equipment",
    howOften: [
      { when: "Bowl, beater, hook and whip: after each use (like other utensils)", citation: SCHED("Cutting boards, knives, utensils") },
      EVERY_4H,
      { when: "Mixer body: keep free of food residue and dust", citation: "Food Code 4-601.11(C)" },
    ],
    steps: ["Take off the bowl and attachments.", "Wash, rinse and sanitize them in the three-compartment sink or dishwasher.", "Air-dry.", "Wipe food off the mixer body and head as the maker says."],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [{ if: "It mixed an allergen (wheat flour, nuts, eggs, milk)", then: ["Wash the bowl and attachments with soap and water before an allergen-free batch.", "Wipe flour dust off the mixer head."], citations: [SP("cross-contact (how it happens: flour dust and crumbs)")] }, C_RAW_MEAT],
    dont: ["Don't put the mixer body in water unless the maker allows it."],
    citations: [SCHED("Cutting boards, knives, utensils"), "Food Code 4-601.11", "Food Code 4-602.11"],
    keywords: ["mixer", "stand mixer", "dough hook", "beater", "whip", "mixing bowl"],
    libraryIds: [L.clean, L.crossContact], requirementIds: [R.sanitize, R.allergens],
  },
  {
    id: "can-opener", name: "Can opener", area: "prep", kind: "food_contact",
    howOften: [IF_CONTAMINATED, EVERY_4H, { when: "Blade must stay clean to sight and touch", citation: "Food Code 4-601.11(A)" }],
    steps: ["Take off the blade and gear as the maker says (or wash a hand opener whole).", ...FOOD_CONTACT_BASICS.slice(1)],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [{ if: "The blade is rusty, nicked or leaves metal bits", then: ["Replace the blade or opener. Surfaces must be corrosion-resistant and in good repair."], citations: ["Food Code 4-101.11"] }],
    dont: ["Don't leave food buildup on the blade."],
    citations: ["Food Code 4-601.11", "Food Code 4-602.11", "Food Code 4-101.11"],
    keywords: ["can opener", "opener", "cans"],
    libraryIds: [L.clean], requirementIds: [R.sanitize],
  },
  {
    id: "pans-containers", name: "Pots, pans, sheet pans and food containers", area: "prep", kind: "food_contact", materials: ["stainless steel", "aluminum", "plastic"],
    howOften: [{ when: "After each use", citation: TABLE("Pots, pans, hotel pans") }],
    steps: ["Scrape food into the trash.", "Soak and scrub baked-on food with hot water and a brush.", "Wash, rinse and sanitize in the three-compartment sink or dishwasher.", "Air-dry upside down.", "Store covered or upside down, 6 inches off the floor."],
    products: [P.dishSoap, P.degreaser, ...SANITIZE_SET],
    contingencies: [
      { if: "Burnt or stuck-on food", then: ["Soak in hot water, then scrub.", "Use an alkaline degreaser or scouring powder (not on nonstick or stone).", "Rinse off all cleaner before sanitizing."], citations: ["Food Code 4-603.14"] },
      { if: "The pan is dented, pitted or rusted", then: ["Replace it."], citations: ["Food Code 4-101.11(E)"] },
      { if: "Cooling food fast", then: ["Use shallow metal pans, 2 inches deep or less."], citations: ["Food Code 3-501.15"] },
    ],
    dont: ["Never put food in trash bags, garbage cans or household buckets.", "Don't stack pans while wet."],
    citations: [TABLE("Pots, pans, hotel pans"), "Food Code 4-603.14", "Food Code 4-101.11", "Food Code 4-903.11"],
    keywords: ["pots", "pans", "sheet pan", "hotel pan", "containers", "food storage containers", "baking sheet", "cambro"],
    libraryIds: [L.sink3, L.dish], requirementIds: [R.sanitize],
  },
  {
    id: "prep-sink", name: "Food prep sink", area: "prep", kind: "food_contact", materials: ["stainless steel"],
    howOften: [{ when: "Before prep, and after thawing raw meat in it", citation: TABLE("Sinks (prep and dish)") }],
    steps: ["Clear the sink.", "Scrub with hot soapy water.", "Rinse.", "Sanitize the bowl and let it air-dry.", "Now rinse produce."],
    products: [...SANITIZE_SET, P.dishSoap, P.stainless],
    contingencies: [
      { if: "Raw meat was thawed or handled in it", then: ["Clean and sanitize the sink before rinsing any produce."], citations: [TABLE("Sinks (prep and dish): after raw meat thawing"), SP("prep order (rinse produce only in a clean, sanitized sink)")] },
      { if: "Someone wants to rinse raw chicken", then: ["Don't. It splashes bacteria around the sink."], citations: [SP("prep order (don't rinse raw chicken or meat)")] },
      { if: "The drain is piped straight to the sewer", then: ["It must drain through an air gap into a floor sink; call a plumber."], citations: one(W.airGapCite) },
    ],
    dont: ["Never wash hands in the prep sink.", "Never dump mop water here."],
    citations: [TABLE("Sinks (prep and dish)"), SP("where to wash hands"), SP("backflow: drains")],
    keywords: ["prep sink", "vegetable sink", "produce sink", "sink", "thawing"],
    libraryIds: [L.clean, L.hands], requirementIds: [R.sanitize, R.produce],
  },
  {
    id: "hand-sink", name: "Handwashing sink", area: "prep", kind: "handwashing",
    howOften: [
      { when: "Daily: clean, sanitize and restock", citation: SCHED("Sink and handwashing station") },
      { when: "Each day or event: clean, sanitize, and restock soap and towels", citation: SCHED("Handwashing sinks") },
    ],
    steps: ["Clean the bowl and faucet.", "Sanitize them.", "Refill soap and paper towels.", "Put a nail brush out.", "Make sure nothing is stored in or blocking it."],
    products: [P.handSoap, P.towels, P.nailBrush, P.spray],
    contingencies: [
      { if: "Out of soap or paper towels", then: ["Restock now. A handwashing sink needs soap and a way to dry hands."], citations: ["Food Code 6-301.11", "Food Code 6-301.12"] },
      { if: "Dishes, food or pitchers are in it", then: ["Take them out. This sink is for hands only."], citations: [SP("where to wash hands"), "Food Code 2-301.15"] },
      { if: "No warm water", then: ["Tell the person in charge and get it fixed. Handwashing sinks need warm water."], citations: ["Food Code 5-202.12"] },
    ],
    dont: ["Never wash dishes, rinse food or dump mop water here.", "Never use dish soap or sanitizer on hands."],
    citations: [SCHED("Sink and handwashing station"), "Food Code 2-301.15", "Food Code 6-301.11", "Food Code 6-301.12"],
    keywords: ["hand sink", "handwashing sink", "hand washing", "soap", "paper towels", "wash hands"],
    libraryIds: [L.hands], requirementIds: [R.hands],
  },

  // ------------------------------------------------------------ COOKING LINE
  {
    id: "range-stovetop", name: "Stove tops and ranges", area: "cooking_line", kind: "equipment", materials: ["stainless steel", "cast iron"],
    howOften: [
      { when: "After each use; deep clean monthly", citation: SCHED("Ovens, range, griddle") },
      COOK_24H,
    ],
    steps: ["Follow the maker's cleaning instructions.", "Scrape off burnt food.", "Degrease as the maker says.", "Wipe off the cleaner.", "Keep it free of built-up grease and food."],
    products: [P.degreaser, P.stainless],
    contingencies: [
      { if: "Built-up grease", then: ["Use an alkaline degreaser, then rinse it off.", "Cooking surfaces must be kept free of encrusted grease."], citations: ["Food Code 4-601.11(B)", "Food Code 4-603.14"] },
    ],
    dont: ["Don't use steel wool on stainless.", "Never mix cleaners (bleach plus ammonia or acid makes toxic gas)."],
    citations: [SCHED("Ovens, range, griddle"), "Food Code 4-601.11(B)", "Food Code 4-602.12(A)", SHOP("Grease, ovens, hoods")],
    keywords: ["stove", "stovetop", "range", "burners", "cooktop", "grates"],
    libraryIds: [L.clean, L.chem], requirementIds: [R.freq, R.chem],
  },
  {
    id: "griddle", name: "Griddle and flat-top", area: "cooking_line", kind: "food_contact", materials: ["steel"],
    howOften: [{ when: "After each use; deep clean monthly", citation: SCHED("Ovens, range, griddle") }, COOK_24H],
    steps: ["Scrape off food and grease as the maker says.", "Degrease per the maker.", "Rinse off the cleaner.", "Keep it free of built-up grease and food."],
    products: [P.degreaser],
    contingencies: [
      { if: "Cooking raw meat and allergen-free or ready-to-eat food on it", then: ["Cook allergen-free food first, on clean foil with clean tongs.", "Keep poultry on its own section, away from burgers, steaks and fish."], citations: [SP("cross-contact, prevention (allergen-free first, on clean foil)"), SP("separate raw meat types (poultry on its own section)")] },
    ],
    dont: ["Don't leave oven cleaner on a cooking surface without rinsing."],
    citations: [SCHED("Ovens, range, griddle"), "Food Code 4-601.11(B)", "Food Code 4-602.12(A)"],
    keywords: ["griddle", "flat top", "flattop", "plancha", "grill top"],
    libraryIds: [L.clean, L.crossContact], requirementIds: [R.freq, R.allergens],
  },
  {
    id: "fryer", name: "Deep fryer", area: "cooking_line", kind: "equipment",
    howOften: [
      { when: "Hot-oil cooking and filtering equipment: on the schedule approved for it (the 24-hour cooking-surface rule doesn't apply)", citation: "Food Code 4-602.11(D)(6)" },
      { when: "Cooking-oil storage tanks and lines: as often as the maker says, or often enough to prevent soil or mold", citation: "Food Code 4-602.11(E)(4)" },
    ],
    steps: [
      "Follow the fryer maker's instructions to drain, filter and clean.",
      "Keep the oil filter easy to reach and replace or clean it.",
      "Store used oil in a sealed container for grease pickup.",
      "Wipe grease off the outside.",
    ],
    products: [P.degreaser],
    contingencies: [
      { if: "Frying shellfish, fish or breaded food", then: ["Don't share that oil with other foods. Use a separate fryer or fresh oil."], citations: [SP("cross-contact, prevention (no shared fryer oil for shellfish, fish or breaded foods)")] },
      { if: "Someone asks for gluten-free fries", then: ["Only call it gluten-free if the fryer is not shared with wheat, barley or rye foods."], citations: [SP("allergen alerts (gluten-free: no shared toaster, fryer or board)")] },
      { if: "When should the oil be changed?", then: ["The Food Code has no oil-change rule. Follow the fryer and oil makers' instructions."], citations: ["Research: menu-items.md (frying-oil quality not in the Food Code)"] },
    ],
    dont: ["Never pour used oil down a drain or on the ground."],
    citations: ["Research: menu-items.md (fryer cleaning; hot-oil filter equipment)", "Food Code 4-202.14", SP("solid waste (used cooking oil in a sealed container for grease pickup)")],
    keywords: ["fryer", "deep fryer", "fry oil", "fries", "frying", "oil"],
    libraryIds: [L.crossContact, L.allergens], requirementIds: [R.freq, R.allergens],
  },
  {
    id: "oven", name: "Ovens", area: "cooking_line", kind: "equipment",
    howOften: [{ when: "After each use; deep clean monthly", citation: SCHED("Ovens, range, griddle") }, COOK_24H],
    steps: ["Follow the maker's cleaning instructions.", "Scrape out burnt food.", "Use oven cleaner exactly as the label says (gloves if it calls for them).", "Rinse off all cleaner.", "Wipe the door and handle."],
    products: [P.degreaser, P.ovenCleaner, P.gloves],
    contingencies: [{ if: "Oven cleaner touched a rack or pan that holds food", then: ["Rinse it off completely before washing and sanitizing."], citations: [SHOP("Grease, ovens, hoods (avoid oven cleaner on food-contact surfaces without rinsing)"), "Food Code 4-603.16"] }],
    dont: ["Don't leave oven cleaner on food-contact surfaces.", "Never mix oven cleaner with other chemicals."],
    citations: [SCHED("Ovens, range, griddle"), "Food Code 4-601.11(B)", SHOP("Grease, ovens, hoods")],
    keywords: ["oven", "convection oven", "baking", "oven racks"],
    libraryIds: [L.clean, L.chem], requirementIds: [R.freq, R.chem],
  },
  {
    id: "microwave", name: "Microwave", area: "cooking_line", kind: "equipment",
    howOften: [COOK_24H, IF_CONTAMINATED],
    steps: ["Wipe out food splatter.", "Wash the inside and turntable with soapy water as the maker says.", "Rinse.", "Wipe the door handle and buttons."],
    products: [P.dishSoap, P.spray],
    contingencies: [{ if: "Heating food in it", then: ["Cover, stir or rotate, heat to 165°F, then let it stand 2 minutes."], citations: ["Food Code 3-401.12"] }],
    dont: ["Don't use glass cleaner or disinfectant on the turntable or other food-contact parts."],
    citations: ["Food Code 4-602.12(A)", "Food Code 4-601.11", "Food Code 3-401.12"],
    keywords: ["microwave", "micro"],
    libraryIds: [L.clean, L.cooking], requirementIds: [R.freq],
  },
  {
    id: "hood-filters", name: "Hood filters and exhaust hood", area: "cooking_line", kind: "equipment", materials: ["stainless steel", "aluminum"],
    howOften: [
      { when: "Filters: monthly, or after heavy frying", citation: SCHED("Hood filters") },
      { when: "Hood and ducts: professional degreasing every 6 months (more often with heavy frying)", citation: SCHED("Hood and exhaust ducts") },
      { when: "Fire-suppression system and extinguishers: inspected every 6 months / yearly", citation: SCHED("Fire-suppression system and extinguishers") },
    ],
    steps: ["Take out the filters.", "Soak them in degreaser and scrub, or run them through the dishwasher.", "Rinse and let them dry.", "Put them back.", "Log the date."],
    products: [P.degreaser],
    contingencies: [{ if: "Grease dripping on walls or ceiling", then: ["Clean the filters now and call the hood cleaner. Hoods must keep grease off walls and ceilings."], citations: [SP("facility requirements (ventilation)")] }],
    dont: ["Don't clean filters over open food or clean dishes."],
    citations: [SCHED("Hood filters"), SCHED("Hood and exhaust ducts"), SP("facility requirements (ventilation)")],
    keywords: ["hood", "hood filters", "exhaust", "vent hood", "grease filter"],
    libraryIds: [L.clean], requirementIds: [R.freq],
  },
  {
    id: "grill-grates", name: "Outdoor grill grates", area: "cooking_line", kind: "food_contact", materials: ["cast iron", "steel"],
    howOften: [{ when: "Before cooking and between raw uses", citation: TABLE("Grill grates") }, { when: "Before and after each event", citation: SCHED("Coolers and outdoor BBQ equipment") }],
    steps: ["Preheat to burn off residue.", "Scrape with a grill brush.", "Oil the grates.", "Check for loose brush bristles on the grates."],
    products: [P.degreaser, P.grillBrush],
    contingencies: [
      { if: "Cooking poultry with other meats", then: ["Keep poultry on its own section, away from burgers, steaks and fish.", "Use separate tongs for each."], citations: [SP("separate raw meat types (on the grill)")] },
      { if: "Cooked meat is ready to come off", then: ["Put it on a clean platter, never the raw one."], citations: [SP("separate raw meat types (never put cooked meat back on a raw platter)")] },
    ],
    dont: ["Never dump grease on grass or into a storm drain.", "Never use a charcoal or propane grill indoors; keep grills 10 feet from buildings."],
    citations: [TABLE("Grill grates"), SP("outdoor barbecue station"), SP("federal agency issues (CPSC)")],
    keywords: ["grill", "bbq", "barbecue", "grill grates", "grate"],
    libraryIds: [L.clean, L.cooking], requirementIds: [R.freq, R.storage],
  },

  // ------------------------------------------------------------ DISH
  {
    id: "three-compartment-sink", name: "Three-compartment sink", area: "dish", kind: "equipment", materials: ["stainless steel"],
    howOften: [{ when: "Clean and sanitize the sinks and drainboards before and after each use", citation: SCHED("Three-compartment sink and drainboards") }],
    steps: [
      "Clean and sanitize all three sinks and both drainboards.",
      "Fill sink 1 with detergent and water at 110°F or hotter.",
      "Fill sink 2 with clean rinse water at 110°F or hotter.",
      "Fill sink 3 with sanitizer and test it: chlorine 50–99 ppm, quat at label strength, or hot water 171°F.",
      "Scrape, wash, rinse, sanitize (chlorine 10+ seconds, quat 30+ seconds), then air-dry upside down.",
    ],
    products: [P.dishSoap, P.quat, P.chlorine, P.strips, P.thermometer],
    contingencies: [
      { if: "Water is dirty, greasy, out of suds or cooling off", then: ["Drain it and refill that sink."], citations: [SP("the three-compartment sink (change the water)")] },
      C_WEAK,
      { if: "Only two sinks or tubs available", then: ["That needs county approval, and only for batches, never a steady flow of dishes during service."], citations: ["Food Code 4-301.12"] },
      C_OUT_SANITIZER,
    ],
    dont: ["Never wash hands, dump mop water, thaw food or rinse produce here while dishes are being washed.", "Never towel-dry.", "Don't skip the rinse (soap weakens sanitizer).", "Never mix chemicals."],
    citations: [SCHED("Three-compartment sink and drainboards"), SP("the three-compartment sink"), "Food Code 4-301.12", "Food Code 4-703.11", "Food Code 4-901.11"],
    keywords: ["three compartment sink", "3 compartment sink", "dish sink", "wash rinse sanitize", "dishes", "pot sink"],
    libraryIds: [L.sink3, L.clean], requirementIds: [R.sanitize],
  },
  {
    id: "dishwasher", name: "Dishwasher (dish machine)", area: "dish", kind: "equipment",
    howOften: [
      { when: "Daily when used: clean scrap trays, spray arms and filters", citation: SCHED("Dishwasher (daily when used)") },
      { when: "Delime monthly, or as the maker says", citation: SCHED("Dishwasher (delime monthly)") },
      { when: "Check temperature gauges or sanitizer strength every day", citation: SP("using a warewashing machine") },
    ],
    steps: ["Scrape and pre-rinse dishes.", "Load so water reaches every surface; no nesting.", "Run a full cycle; don't open it mid-cycle.", "Check the gauges or test the sanitizer.", "Air-dry in the racks.", "At day's end, clean scrap trays, spray arms and filters."],
    products: [P.dishwasher, P.strips],
    contingencies: [
      { if: "Temperature or sanitizer is too low", then: ["Stop using the machine.", "Switch to the three-compartment sink until it's repaired."], citations: [SP("using a warewashing machine (switch to the three-compartment sink)")] },
      { if: "Home dishwasher with no sanitize cycle", then: ["Sanitize by hand after washing."], citations: [SP("using a home dishwasher")] },
    ],
    dont: ["Don't use hand dish soap in a dishwasher.", "Don't stack dishes wet."],
    citations: [SCHED("Dishwasher"), SP("using a warewashing machine"), "Food Code 4-501.110", "Food Code 4-501.112"],
    keywords: ["dishwasher", "dish machine", "warewasher", "dishes"],
    libraryIds: [L.dish], requirementIds: [R.sanitize],
  },
  {
    id: "wiping-cloth-bucket", name: "Wiping cloths and sanitizer buckets", area: "dish", kind: "equipment",
    howOften: [
      { when: "Test the bucket when mixed and every 2 hours", citation: SP("wiping cloths (test the bucket every 2 hours)") },
      { when: "Launder wet cloths daily", citation: SP("laundering schedule (wet wiping cloths: daily)") },
      { when: "Change cloths when soiled", citation: TABLE("Wiping cloths") },
    ],
    steps: ["Mix sanitizer at label strength and test it with the cloths in it.", "Keep cloths in the bucket between uses.", "Rinse a soiled cloth in clean water before putting it back.", "Keep the bucket off the floor and away from food.", "Launder wet cloths daily in hot water."],
    products: [P.cloths, P.quat, P.strips],
    contingencies: [
      C_WEAK,
      { if: "Cotton cloths keep testing low in quat", then: ["Switch to microfiber or quat-compatible cloths, or switch to chlorine."], citations: [SP("wiping cloths (quat binding)")] },
      { if: "A cloth was used on raw meat surfaces", then: ["Keep it separate from all other cloths (use a distinct color)."], citations: [SP("wiping cloths (raw meat, poultry, fish or eggs)")] },
    ],
    dont: ["A cloth swipe doesn't sanitize a food-contact surface.", "Never put the bucket on a prep table beside open food.", "Never use restroom cloths in the kitchen."],
    citations: [SP("wiping cloths and laundry"), "Food Code 3-304.14", "Food Code 4-802.11"],
    keywords: ["wiping cloth", "rag", "towel", "sanitizer bucket", "bucket", "cloth"],
    libraryIds: [L.cloths], requirementIds: [R.tools, R.sanitize],
  },

  // ------------------------------------------------------------ COLD STORAGE
  {
    id: "reach-in-fridge", name: "Reach-in refrigerator", area: "cold_storage", kind: "equipment",
    howOften: [
      { when: "Shelves and handles: weekly and after spills", citation: SCHED("Refrigerator shelves and handles") },
      { when: "Weekly wipe-down; deep clean monthly; defrost as needed", citation: SCHED("Refrigerators and freezers") },
    ],
    steps: ["Move food to another cold unit.", "Take out shelves.", "Wash, rinse and sanitize shelves, walls, handles and gaskets.", "Air-dry and put back.", "Restock in fridge order: ready-to-eat on top, then raw seafood, whole beef and pork, ground meat, and raw poultry on the bottom.", "Check the fridge thermometer reads 41°F or below."],
    products: [...SANITIZE_SET, P.dishSoap, P.thermometer, P.fridgeThermo],
    contingencies: [
      { if: "The fridge reads above 41°F", then: ["Move TCS food to a working fridge.", "Throw out any TCS food above 41°F for more than 4 hours, or for an unknown time.", "Fix the fridge before using it again."], citations: [SP("corrective actions (fridge above 41°F)")] },
      { if: "Raw meat juice dripped on a shelf", then: ["Wash, rinse and sanitize the shelf.", "Put raw meat in a pan or sealed container on the bottom shelf."], citations: ["Food Code 4-602.11", SP("refrigerator stacking order")] },
      { if: "Power outage", then: ["Keep the door shut.", "Throw out TCS food above 41°F for more than 4 hours."], citations: [SP("occasions for temperature abuse (power outage)")] },
    ],
    dont: ["Don't overpack; leave room for cold air.", "Never store raw meat above ready-to-eat food."],
    citations: [SCHED("Refrigerators and freezers"), SCHED("Refrigerator shelves and handles"), "Food Code 3-302.11", "Food Code 3-501.16"],
    keywords: ["fridge", "refrigerator", "reach in", "cooler", "shelves", "handles"],
    libraryIds: [L.fridge, L.holding], requirementIds: [R.storage, R.holding],
  },
  {
    id: "walk-in", name: "Walk-in cooler", area: "cold_storage", kind: "equipment",
    howOften: [
      { when: "Weekly wipe-down; deep clean monthly", citation: SCHED("Refrigerators and freezers") },
      { when: "Shelves: monthly, with a FIFO check", citation: SCHED("Storage shelves (dry, walk-in)") },
      { when: "Equipment used inside a room at 41°F or less: clean at least every 24 hours", citation: "Food Code 4-602.11 (refrigerated rooms)" },
    ],
    // The fridge order (owner: "reuse this diagram … the walk-in steps"): the same one step as the reach-in, library
    // fridge-storage-order, Food Code 3-302.11 (cited below).
    steps: ["Empty one shelf at a time.", "Wipe and sanitize the shelf.", "Check dates; oldest to the front.", "Restock in fridge order: ready-to-eat on top, then raw seafood, whole beef and pork, ground meat, and raw poultry on the bottom.", "Sweep and mop the floor with the kitchen mop.", "Keep food 6 inches off the floor."],
    products: [...SANITIZE_SET, P.floorKitchen, P.kitchenMop],
    contingencies: [
      { if: "Walk-in reads above 41°F", then: ["Move TCS food to working cold storage.", "Throw out TCS food above 41°F for more than 4 hours or unknown time."], citations: [SP("corrective actions (fridge above 41°F)")] },
      { if: "Raw meat juice dripped on a shelf", then: ["Wash, rinse and sanitize the shelf.", "Put raw meat in a pan or sealed container on the bottom shelf."], citations: ["Food Code 4-602.11", SP("refrigerator stacking order")] },
      { if: "Condensate line drains straight into the sewer", then: ["Have a plumber add an air gap (indirect drain)."], citations: one(W.airGapCite) },
      C_PESTS,
    ],
    dont: ["Never put cases or sacks directly on the floor.", "Don't pack it tight; leave room for cold air to move."],
    citations: [SCHED("Refrigerators and freezers"), SCHED("Storage shelves (dry, walk-in)"), "Food Code 4-602.11", "Food Code 3-305.11", "Food Code 3-302.11"],
    keywords: ["walk in", "walk-in", "walk in cooler", "cooler room"],
    libraryIds: [L.fridge, L.dry], requirementIds: [R.storage, R.holding],
  },
  {
    id: "freezer", name: "Freezer", area: "cold_storage", kind: "equipment",
    howOften: [{ when: "Weekly wipe-down; deep clean monthly; defrost as needed", citation: SCHED("Refrigerators and freezers") }],
    steps: ["Move food to another freezer.", "Defrost if ice has built up.", "Wash, rinse and sanitize shelves and gaskets.", "Restock: soonest-dated in front, new stock behind.", "Check it reads 0°F or below."],
    products: [...SANITIZE_SET, P.thermometer],
    contingencies: [{ if: "Food thawed during an outage", then: ["Only food thawed in the fridge may be refrozen.", "TCS food above 41°F for more than 4 hours gets thrown out."], citations: [SP("thawing (only fridge-thawed food may be refrozen)"), SP("occasions for temperature abuse")] }],
    dont: ["Keep ready-to-eat food above raw meat."],
    citations: [SCHED("Refrigerators and freezers"), SP("freezer organization and FIFO")],
    keywords: ["freezer", "chest freezer", "frozen"],
    libraryIds: [L.fridge, L.dry], requirementIds: [R.storage],
  },
  {
    id: "door-gaskets", name: "Refrigerator and freezer door gaskets", area: "cold_storage", kind: "non_food_contact", materials: ["rubber"],
    howOften: [{ when: "Monthly deep clean with the shelves", citation: SCHED("Refrigerators and freezers (shelves and gaskets)") }, NFC_CLEAN],
    steps: ["Open the door fully.", "Wash the gasket folds with soapy water.", "Rinse with clean water.", "Dry and check it seals."],
    products: [P.dishSoap, P.spray],
    contingencies: [{ if: "The gasket is torn, cracked or moldy", then: ["Clean off mold now; replace a torn or cracked gasket (follow the maker)."], citations: ["Food Code 4-101.11", "Food Code 4-602.11(E)"] }],
    dont: ["Don't let mold build up in the folds."],
    citations: [SCHED("Refrigerators and freezers"), "Food Code 4-601.11(C)"],
    keywords: ["gasket", "door seal", "fridge door", "rubber seal"],
    libraryIds: [L.fridge], requirementIds: [R.freq],
  },
  {
    id: "coolers-ice-chests", name: "Coolers and ice chests", area: "cold_storage", kind: "food_contact", materials: ["plastic"],
    howOften: [{ when: "Before and after each event, and after raw meat", citation: TABLE("Coolers") }],
    steps: ["Drain it.", "Wash inside with soapy water.", "Rinse.", "Sanitize inside.", "Drain and air-dry with the lid open."],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "It held raw meat", then: ["Wash, rinse and sanitize before it holds drinks or ready-to-eat food.", "Better: one cooler for raw meat, another for ready-to-eat food and drinks."], citations: [TABLE("Coolers: after raw meat"), SP("refrigerator and cooler stacking order")] },
      { if: "Drink ice and cooling ice in the same cooler", then: ["Keep them separate."], citations: [SP("barbecue (drink ice and cooling ice stay separate)")] },
    ],
    dont: ["Never wash hands in a drink cooler or ice chest.", "Don't set coolers of food on the ground."],
    citations: [TABLE("Coolers"), SCHED("Coolers and outdoor BBQ equipment")],
    keywords: ["cooler", "ice chest", "igloo", "picnic cooler"],
    libraryIds: [L.fridge, L.clean], requirementIds: [R.sanitize, R.storage],
  },

  // ------------------------------------------------------------ DRY STORAGE
  {
    id: "storage-shelves", name: "Storage shelves", area: "dry_storage", kind: "non_food_contact", materials: ["metal", "plastic"],
    howOften: [{ when: "Monthly: empty, wipe, FIFO check", citation: SCHED("Storage shelves (dry, walk-in)") }],
    steps: ["Empty the shelf.", "Wipe it with soapy water, then dry it.", "Sanitize it only if unwrapped food sits on it.", "Check dates and throw out anything expired (log it).", "Restock: oldest in front, new behind.", "Keep the bottom shelf 6 inches off the floor, a few inches from the walls."],
    products: [P.spray, P.dishSoap, P.cloths],
    contingencies: [C_PESTS, { if: "Storeroom is above 70°F", then: ["Move dry goods somewhere cooler (50–70°F)."], citations: [SP("dry storage")] }],
    dont: ["Never store food under stairs, sewer lines, in restrooms or mechanical rooms.", "Never store chemicals above food."],
    citations: [SCHED("Storage shelves (dry, walk-in)"), "Food Code 3-305.11", "Food Code 3-305.12", "Food Code 7-201.11", "Food Code 4-602.13"],
    keywords: ["shelves", "shelving", "pantry", "storeroom", "dry storage", "rack"],
    libraryIds: [L.dry, L.pests], requirementIds: [R.storage, R.pests],
  },
  {
    id: "dry-storage-bins", name: "Bulk food bins and dry storage containers", area: "dry_storage", kind: "food_contact", materials: ["plastic"],
    howOften: [NON_TCS_EQUIP, IF_CONTAMINATED],
    steps: ["Empty the bin.", ...FOOD_CONTACT_BASICS.slice(1), "Refill, label with the common name, and keep the scoop outside the product."],
    products: [...SANITIZE_SET, P.dishSoap],
    contingencies: [
      { if: "Beetles, moths or webbing in flour, sugar, rice or beans", then: ["Throw out the food.", "Inspect all nearby stock.", "Clean the bin before refilling."], citations: [SP("action thresholds (stored-product insects)")] },
      { if: "The scoop is sitting in the flour or sugar", then: ["Take it out; store scoops with handles outside the product."], citations: [SP("dry storage (scoops with handles, stored outside the product)")] },
    ],
    dont: ["Don't use bins without tight lids.", "Don't store bins on the floor."],
    citations: [SP("facility requirements (bulk food bins)"), SP("dry storage"), "Food Code 4-602.11(E)"],
    keywords: ["bins", "flour bin", "sugar bin", "bulk bins", "containers", "dry goods"],
    libraryIds: [L.dry], requirementIds: [R.storage],
  },

  // ------------------------------------------------------------ FRONT OF HOUSE
  {
    id: "pastry-display-case", name: "Pastry display case and trays", area: "front_of_house", kind: "food_contact", materials: ["glass", "stainless steel"],
    howOften: [{ when: "Daily", citation: SCHED("Pastry display case, trays, tongs") }],
    steps: ["Take out pastries and trays.", "Wash, rinse and sanitize the trays and the inside surfaces food touches.", "Clean the outside glass with glass cleaner.", "Air-dry, then restock with nut pastries on their own tray."],
    products: [...SANITIZE_SET, P.glass],
    contingencies: [{ if: "Cream-filled pastries in an unrefrigerated case", then: ["Use a refrigerated case at 41°F or below, or mark a 4-hour discard time."], citations: [SP("occasions for temperature abuse (pastry display)")] }],
    dont: ["Keep glass cleaner off trays and anything food touches.", "Don't let customers help themselves to unwrapped pastries."],
    citations: [SCHED("Pastry display case, trays, tongs"), SP("display case"), SHOP("Glass display case, windows")],
    keywords: ["display case", "pastry case", "bakery case", "trays", "sneeze guard"],
    libraryIds: [L.buffet, L.clean], requirementIds: [R.sanitize, R.self],
  },
  {
    id: "condiment-station", name: "Self-service condiment station and milk carafes", area: "front_of_house", kind: "food_contact",
    howOften: [
      { when: "Condiment dispensers and display containers: before restocking", citation: "Food Code 4-602.11(E)" },
      { when: "Self-service tongs, scoops and ladles for non-TCS food: at least every 24 hours", citation: "Food Code 4-602.11(E)" },
      { when: "Milk carafes in ice at 41°F or below: replace every 4 hours", citation: SP("displayed foods (milk carafes)") },
    ],
    steps: ["Empty and wash dispensers before refilling them.", "Rinse and sanitize.", "Refill; don't top off old product.", "Set milk carafes in ice and mark the swap time.", "Wipe the counter."],
    products: [...SANITIZE_SET, P.cloths],
    contingencies: [{ if: "A guest dipped their own cup into a shared container", then: ["Replace that container. Personal cups never dip into shared drinks."], citations: [SP("refilling personal cups (don't dip personal cups)")] }],
    dont: ["Don't top off a dirty dispenser."],
    citations: ["Food Code 4-602.11(E)", SP("displayed foods"), "Food Code 3-306.13"],
    keywords: ["condiment", "creamer", "milk carafe", "self service", "sugar station", "ketchup", "dispenser"],
    libraryIds: [L.buffet], requirementIds: [R.self, R.holding],
  },
  {
    id: "dining-tables", name: "Dining tables", area: "front_of_house", kind: "non_food_contact", materials: ["plastic", "laminate", "wood", "metal"],
    howOften: [{ when: "Before and after the meal: wash with detergent; sanitize if food touches them directly", citation: TABLE("Dining tables") }],
    steps: ["Clear dishes and crumbs.", "Wash with detergent and a clean cloth.", "Sanitize if food will touch the table directly.", "Let it air-dry."],
    products: [P.dishSoap, P.spray, P.woodFurniture, P.cloths],
    contingencies: [
      { if: "Food goes straight on the table (no plate)", then: ["Sanitize it with a food-contact sanitizer after washing."], citations: [TABLE("Dining tables: sanitize if food touches them directly")] },
      { if: "The table is wood", then: ["Clean with a wood cleaner such as diluted Murphy Oil Soap. Don't soak it with water or use bleach."], citations: [SHOP("Wood coffee tables and furniture")] },
      C_VOMIT,
    ],
    dont: ["Don't use the restroom cloth on tables.", "Don't use disinfectant wipes where food touches unless the label allows it."],
    citations: [TABLE("Dining tables"), SHOP("Plastic and metal chairs and folding tables")],
    keywords: ["table", "dining table", "tables", "tabletop", "booth"],
    libraryIds: [L.clean, L.cloths], requirementIds: [R.sanitize],
  },
  {
    id: "chairs", name: "Dining chairs", area: "front_of_house", kind: "touch_point", materials: ["plastic", "metal", "wood", "upholstered"],
    howOften: [NFC_CLEAN],
    steps: ["Wipe crumbs and spills off the seat and back.", "Plastic or metal: wash with Dawn and warm water.", "Disinfect touch points with a disinfecting wipe.", "Wood: use a wood cleaner. Upholstered: use an upholstery cleaner (test a hidden spot)."],
    products: [P.touchWipes, P.dishSoap, P.woodFurniture, P.upholstery],
    contingencies: [C_VOMIT],
    dont: ["No abrasive pads on plastic.", "Don't soak fabric."],
    citations: [SHOP("Plastic and metal chairs and folding tables"), SHOP("Upholstered chairs"), "Food Code 4-601.11(C)"],
    keywords: ["chair", "chairs", "seat", "stool", "bench"],
    libraryIds: [L.clean], requirementIds: [R.chem],
  },
  // ---- Upholstered seating (booths, couches, chairs): by material, the tag codes, spills. Sources: docs/source/research/upholstery-seating.md
  {
    id: "leather-seating", name: "Leather couch or chair", area: "front_of_house", kind: "non_food_contact", materials: ["leather", "genuine leather"],
    howOften: [
      { when: "Dust and vacuum the seams often; wipe weekly with a damp, soft cloth", citation: UPH("UP10, UP11") },
      { when: "A spill or accident: clean it right away, even while food is out", citation: "Food Code 6-501.12(B)" },
      { when: "Everything else: as often as needed to keep it clean", citation: "Food Code 6-501.12(A)" },
    ],
    steps: [
      "Check that it is real leather. Pleather has its own guide.",
      "Find the care sheet or tag. It wins over this guide.",
      "Test in a hidden spot first. Let it dry.",
      "Use a crevice tool to vacuum the seams.",
      "Wipe with a damp, soft, lint-free cloth. Don't rub.",
      "Wipe the whole cushion, edge to edge.",
      "Let it air-dry, away from sun and heat.",
      "Wash your hands before you go back to food or clean dishes.",
    ],
    products: [{ purpose: "Leather cleaner and conditioner the maker names (test a hidden spot)", examples: ["Millennium Leather Care Kit (Ashley's own kit)", "The leather cleaner on the maker's care sheet"], note: "Use only what the maker's sheet names. Brands are examples, not endorsements." }, { purpose: "Soft, lint-free cloths", examples: ["The cloths the maker names"] }],
    contingencies: [
      { if: "A fresh spill (soda, coffee, sauce)", then: ["Wipe it up right away with a clean, soft cloth.", "Wipe the whole cushion edge to edge with a damp cloth so it dries without a ring."], citations: [UPH("UP11, UP15"), "Food Code 6-501.12(B)"] },
      { if: "A small, fresh oil or grease stain", then: ["Rub corn starch into the stain with your fingers.", "Vacuum or brush the powder off.", "Repeat until the stain is gone."], citations: [UPH("UP16")] },
      { if: "Someone threw up or had an accident on it", then: ["Stop and follow the vomit and diarrhea cleanup guide first: keep people away, put on gloves, scoop it up, bag it.", "Do not put bleach or a food-contact sanitizer on leather.", "Our sources name no disinfectant that is safe on leather. Call the maker or a leather professional for the next step."], citations: ["Food Code 2-501.11", UPH("UP17, UP18")] },
      { if: "You have no care sheet", then: ["Use only a damp, soft cloth.", "Call the seller or the maker and ask which leather cleaner they name."], citations: [UPH("UP12, UP13")] },
    ],
    dont: [
      "Don't use bleach, a food-contact sanitizer or disinfectant spray on leather.",
      "Don't use saddle soap, oil, soap or detergent leather cleaners, abrasives or household cleaners.",
      "Don't soak it or scrub it.",
      "Don't leave it in direct sun or within two feet of a heat source.",
    ],
    citations: [UPH("UP10-UP18"), "Food Code 6-501.12"],
    softSurface: true,
    keywords: ["leather", "genuine leather", "leather couch", "leather sofa", "leather chair", "leather booth", "leather seat", "couch", "sofa", "loveseat", "upholstery", "cushion", "restaurant booth"],
    libraryIds: [L.vomit], requirementIds: [R.freq],
  },
  {
    id: "faux-leather-seating", name: "Pleather and vinyl seating", area: "front_of_house", kind: "non_food_contact", materials: ["faux leather", "pleather", "vinyl", "polyurethane", "PU", "PVC", "vinyl-coated fabric"],
    howOften: [
      { when: "Wipe booths and seats down when they look or feel soiled", citation: "Food Code 6-501.12(A)" },
      { when: "A spill or accident: clean it right away, even while food is out", citation: "Food Code 6-501.12(B)" },
    ],
    steps: [
      "Find the care sheet. For a booth, ask the installer.",
      "Test in a hidden spot first. Let it dry.",
      "Wipe off crumbs and spills with a white cloth.",
      "Use a soft white cloth, not paper towels.",
      "Wash with mild soap and warm water.",
      "Wipe again with plain water to rinse.",
      "Let it air-dry.",
      "Wash your hands before you go back to food or clean dishes.",
    ],
    products: [{ purpose: "Mild soap and soft white cloths", examples: ["Mild liquid soap", "Soft white cloths, or what the maker names"], note: "Store-bought sprays can harm vinyl. Use what the maker's sheet names." }],
    contingencies: [
      { if: "A hard stain (ink, grease, lipstick) and the maker's sheet allows bleach", then: ["Naugahyde, a vinyl maker, wipes its own vinyl with household bleach and water (10% bleach), then rinses with a water-dampened cloth. That is one maker's method for its product, not a rule for all faux leather.", "Wear gloves. Test a hidden spot first. Keep bleach away from food, dishes and food-contact surfaces."], citations: [UPH("UP23, UP21")] },
      { if: "The cover is cracked, torn or peeling", then: ["Don't use the seat until it is repaired or re-covered. A torn seat holds crumbs and spills and can't be cleaned well.", "Ask the maker or an upholsterer about a repair."], citations: ["Food Code 6-501.11", UPH("UP33")] },
      { if: "Someone threw up or had an accident on it", then: ["Stop and follow the vomit and diarrhea cleanup guide first: keep people away, put on gloves, scoop it up, bag it.", "For the disinfectant, use what the maker's care sheet allows on that seat. Naugahyde's own sheet names bleach and water for its vinyl.", "If you have no sheet, ask the maker before you use bleach or a sanitizer on it."], citations: ["Food Code 2-501.11", UPH("UP23, UP24")] },
    ],
    dont: [
      "Don't use bleach or a food-contact sanitizer on a seat unless its maker's sheet allows it.",
      "Don't use paper towels, abrasive pads or industrial solvents.",
      "Don't use store-bought sprays the maker doesn't name. They can harm vinyl.",
    ],
    citations: [UPH("UP19-UP25"), "Food Code 6-501.12"],
    softSurface: true,
    keywords: ["pleather", "faux leather", "fake leather", "vegan leather", "vinyl", "vinyl seat", "polyurethane", "pvc", "leatherette", "booth", "restaurant booth", "booth seat", "faux leather couch", "pleather couch", "pleather booth", "upholstery"],
    libraryIds: [L.vomit], requirementIds: [R.freq],
  },
  {
    id: "fabric-seating", name: "Fabric seating, polyester and microfiber", area: "front_of_house", kind: "non_food_contact", materials: ["polyester", "microfiber", "fabric", "cloth", "upholstered"],
    howOften: [
      { when: "Vacuum or brush off crumbs often", citation: UPH("UP5, UP8") },
      { when: "A spill or accident: blot it right away, even while food is out", citation: "Food Code 6-501.12(B)" },
    ],
    steps: [
      "Find the cleaning code on the tag.",
      "Test in a hidden spot first. Let it dry.",
      "Use a vacuum or brush to lift crumbs.",
      "Blot a spill with a clean white cloth.",
      "Blot W or W/S cloth with mild soap and water.",
      "Blot with clean water, then blot dry.",
      "Use only a water-free solvent on code S cloth.",
      "Use no liquid on code X cloth.",
      "Wash your hands before you go back to food or clean dishes.",
    ],
    products: [P.upholstery, P.dishSoap, { purpose: "Clean white cloths", examples: ["The cloths the maker names, or clean white ones"] }],
    contingencies: [
      { if: "The tag is gone or says nothing", then: ["Blot the spill with a dry, clean white cloth and vacuum. Add no liquid.", "Ask the seller or the maker for the cleaning code."], citations: [UPH("UP5, UP30")] },
      { if: "Someone threw up or had an accident on it", then: ["Stop and follow the vomit and diarrhea cleanup guide first. Scoop up the solids and bag them.", "Then clean the fabric by its code. Check the maker's sheet before you use bleach or a sanitizer on cloth."], citations: ["Food Code 2-501.11", UPH("UP29")] },
      { if: "A water ring shows after cleaning", then: ["Dampen the whole panel the same way, edge to edge, so it dries evenly. Code X cloth: call a professional."], citations: [UPH("UP8, UP32")] },
    ],
    dont: ["Don't soak the fabric.", "Don't rub. Blot.", "Don't use water on code S or code X cloth.", "Don't use a solvent near a flame."],
    citations: [UPH("UP2-UP9, UP30-UP32"), "Food Code 6-501.12"],
    softSurface: true,
    keywords: ["fabric", "polyester", "microfiber", "polyester chair", "microfiber couch", "fabric couch", "fabric chair", "fabric booth", "cloth seat", "upholstery", "upholstered", "couch", "sofa", "chair", "cushion"],
    libraryIds: [L.vomit], requirementIds: [R.freq],
  },
  {
    id: "upholstery-codes", reference: true, name: "Upholstery cleaning codes on the tag", area: "front_of_house", kind: "non_food_contact", materials: ["fabric", "upholstered"],
    howOften: [{ when: "As often as needed to keep it clean", citation: "Food Code 6-501.12(A)" }, { when: "A spill or accident: right away, even while food is out", citation: "Food Code 6-501.12(B)" }],
    steps: [
      "Find the tag under a seat cushion.",
      "Read the code: W, S, W/S or X.",
      "Test in a hidden spot first. Let it dry.",
      "Use water-based products only on code W.",
      "Use a water-free solvent only on code S.",
      "Use either one on code W/S. Start with the gentlest.",
      "Use no liquid on code X. Vacuum or brush only.",
    ],
    products: [P.upholstery],
    contingencies: [
      { if: "There is no tag (a booth, a used chair)", then: ["Ask who made or installed it for the care sheet.", "Until you know, blot with a dry white cloth and vacuum. Add no liquid."], citations: [UPH("UP1, UP5")] },
      { if: "The piece is leather or vinyl", then: ["The W, S and X codes are for cloth. Use the leather guide or the faux leather and vinyl guide."], citations: [UPH("UP10-UP25")] },
    ],
    dont: ["Don't use water on S or X.", "Don't use a solvent on W.", "Don't skip the hidden-spot test."],
    citations: [UPH("UP1-UP9")],
    softSurface: true,
    keywords: ["upholstery code", "upholstery cleaning code", "cleaning code", "cleaning codes", "code w", "code s", "code x", "w/s", "ws", "tag", "upholstery tag", "fabric tag", "couch tag", "what does w mean", "what does x mean"],
    libraryIds: [], requirementIds: [R.freq],
  },
  {
    id: "seating-spills", name: "Spills, vomit and body fluids on seating", area: "front_of_house", kind: "non_food_contact", materials: ["leather", "vinyl", "fabric"],
    howOften: [{ when: "A spill or accident: right away, even while food is out", citation: "Food Code 6-501.12(B)" }],
    steps: [
      "Keep guests off the seat.",
      "Put on gloves for vomit or diarrhea.",
      "Scoop up solids. Blot liquids.",
      "Test in a hidden spot first.",
      "Open the vomit cleanup guide for body fluids.",
      "Clean the seat to match its material.",
      "Wash your hands before you go back to food or clean dishes.",
    ],
    products: [P.gloves, { purpose: "Clean white cloths", examples: ["The cloths the maker names, or clean white ones"] }],
    contingencies: [
      { if: "Vomit or diarrhea on the seat", then: ["Open the vomit and diarrhea cleanup guide and follow it first.", "Then clean the seat by its material. Don't put bleach or a food-contact sanitizer on leather."], citations: ["Food Code 2-501.11", UPH("UP17, UP18")] },
      { if: "A food or drink spill", then: ["Blot it up right away with a clean cloth.", "Then clean the seat by its material."], citations: ["Food Code 6-501.12(B)"] },
    ],
    dont: ["Don't use bleach or a food-contact sanitizer on leather.", "Don't rub a spill into the seat."],
    citations: ["Food Code 6-501.12", "Food Code 2-301.14(I)", UPH("UP26-UP28")],
    softSurface: true,
    keywords: ["spill", "spills", "vomit on couch", "vomit on booth", "vomit on chair", "throw up", "accident", "body fluid", "body fluids", "seat spill", "booth spill", "couch spill", "blood", "urine", "upholstery"],
    libraryIds: [L.vomit], requirementIds: [R.vomit],
  },
  {
    id: "seating-material", name: "Couch, chair or booth by material", area: "front_of_house", kind: "non_food_contact", materials: ["leather", "faux leather", "pleather", "vinyl", "polyester", "microfiber", "fabric"],
    howOften: [{ when: "As often as needed to keep it clean", citation: "Food Code 6-501.12(A)" }, { when: "A spill or accident: right away, even while food is out", citation: "Food Code 6-501.12(B)" }],
    steps: [
      "Check the tag, or ask the maker.",
      "Test in a hidden spot first. Let it dry.",
      "Ask the maker before you use any chemical.",
      "Use only a damp, soft, lint-free cloth on real leather.",
      "Wipe pleather or vinyl with mild soap and water.",
      "Read the code on cloth seats, then clean by it.",
      "Open the guide for your material.",
    ],
    products: [P.dishSoap],
    contingencies: [
      { if: "The seat is a restaurant booth with no tag", then: ["Ask who made or installed it for the care sheet.", "Until you know, wipe with a damp white cloth and mild soap, and test a hidden spot first."], citations: [UPH("UP19, UP21")] },
    ],
    dont: ["Don't use bleach or a food-contact sanitizer on leather.", "Don't use one cleaner on every seat.", "Don't skip the hidden-spot test."],
    citations: [UPH("UP1, UP10-UP25")],
    softSurface: true,
    keywords: ["couch", "sofa", "loveseat", "chair", "booth", "restaurant booth", "leather", "pleather", "faux leather", "vinyl", "polyester", "microfiber", "fabric", "upholstery", "material", "which cleaner"],
    libraryIds: [], requirementIds: [R.freq],
  },
  {
    id: "high-chairs", name: "High chairs and booster seats", area: "front_of_house", kind: "touch_point", materials: ["plastic"],
    howOften: [{ when: "Before and after each meal: wash; sanitize the tray if food touches it directly (the dining-table rule)", citation: TABLE("Dining tables") }],
    steps: ["Wipe off food.", "Wash the tray and seat with detergent and water.", "Rinse.", "Sanitize the tray with a food-contact sanitizer.", "Let it air-dry."],
    products: [P.dishSoap, P.spray],
    contingencies: [C_VOMIT],
    dont: ["Don't use disinfectant wipes on the tray unless the label allows food contact."],
    citations: [TABLE("Dining tables"), SP("legal criteria for sanitizers (in practice)")],
    keywords: ["high chair", "highchair", "booster", "booster seat", "baby", "kids"],
    libraryIds: [L.clean], requirementIds: [R.sanitize],
  },
  {
    id: "menus", name: "Menus and table tents", area: "front_of_house", kind: "touch_point",
    howOften: [NFC_CLEAN],
    steps: ["Wipe off food and sticky spots with a clean cloth.", "Use a cleaner the menu cover's maker allows.", "Wash hands after handling a stack of used menus, before touching food or clean dishes."],
    products: [P.touchWipes, P.cloths],
    contingencies: [{ if: "Serving anything raw or undercooked on request", then: ["The menu or a table tent must carry the consumer advisory, except for a whole, intact beef steak seared top and bottom to 145°F. At a place that serves a highly susceptible population this food may not be served at all."], citations: ["Food Code 3-603.11"] }],
    dont: ["Keep disinfectant, glass cleaner and polish off dishes and food-contact surfaces."],
    citations: ["Food Code 4-601.11(C)", "Food Code 2-301.14"],
    keywords: ["menu", "menus", "table tent", "menu board"],
    libraryIds: [L.hands], requirementIds: [R.hands],
  },
  {
    id: "payment-screen", name: "Payment screens and POS", area: "front_of_house", kind: "touch_point",
    howOften: [{ when: "Every few hours", citation: SCHED("Door handles, payment screen, light switches") }],
    steps: ["Wipe with a sanitizer or disinfectant wipe the screen's maker allows.", "Let it dry.", "Wash hands (or change gloves) after taking cash or tapping the screen, before touching food."],
    products: [P.touchWipes, P.spray, P.gloves],
    contingencies: [{ if: "You touched cash, a card or the screen while wearing gloves", then: ["Change gloves and wash hands before touching food or drinks."], citations: [SP("changing gloves (taking cash, a card or tapping a phone payment screen)")] }],
    dont: ["Don't go from the register to ready-to-eat food without washing."],
    citations: [SCHED("Door handles, payment screen, light switches"), "Food Code 2-301.14", "Food Code 3-304.15"],
    keywords: ["pos", "payment", "card reader", "register", "cash register", "screen", "tablet", "ipad"],
    libraryIds: [L.gloves, L.hands], requirementIds: [R.hands, R.bare],
  },
  {
    id: "door-handles", name: "Door handles, light switches and faucet handles", area: "front_of_house", kind: "touch_point",
    howOften: [{ when: "Every few hours", citation: SCHED("Door handles, payment screen, light switches") }, { when: "Every few hours during events (also cooler lids and faucet handles)", citation: TABLE("Door handles, cooler lids, light switches, faucet handles") }],
    steps: ["Wipe with an EPA-registered disinfectant or sanitizer.", "Leave it wet for the label time.", "Wash hands after, before touching food."],
    products: [P.touchWipes],
    contingencies: [C_VOMIT],
    dont: ["Don't use the restroom cloth on kitchen handles."],
    citations: [SCHED("Door handles, payment screen, light switches"), TABLE("Door handles, cooler lids, light switches, faucet handles")],
    keywords: ["door handle", "doorknob", "light switch", "handles", "push plate", "faucet handle"],
    libraryIds: [L.clean], requirementIds: [R.chem],
  },
  {
    id: "windows-glass", name: "Windows, glass doors and mirrors", area: "front_of_house", kind: "non_food_contact", materials: ["glass"],
    howOften: [NFC_CLEAN],
    steps: ["Clean the glass with a glass cleaner.", "Check window screens are intact and in good repair.", "Keep glass cleaner away from food, dishes and food-contact surfaces."],
    products: [P.glass],
    contingencies: [{ if: "A screen is torn", then: ["Repair it: windows and vents screened at least 16 mesh per inch."], citations: [SP("inspection checklist (windows and vents screened, 16 mesh)")] }],
    dont: ["Never use glass cleaner on food-contact surfaces."],
    citations: [SHOP("Glass, mirrors, windows, display cases"), "Food Code 4-601.11(C)"],
    keywords: ["windows", "glass", "mirror", "glass door", "window"],
    libraryIds: [L.pests], requirementIds: [R.pests],
  },

  // ------------------------------------------------------------ RESTROOM
  {
    id: "restroom-toilets", name: "Restroom toilets and urinals", area: "restroom", kind: "non_food_contact",
    howOften: [{ when: "Before and after each service day or event: clean and disinfect", citation: SCHED("Restrooms") }],
    steps: ["Put on gloves if the cleaner's label calls for them.", "Clean the bowl with toilet bowl cleaner.", "Spray seat, handle and outside with disinfectant; leave wet for the label time.", "Wipe with a restroom-only cloth.", "Restock toilet paper and empty the trash.", "Wash hands."],
    products: [P.restroom, P.toiletPaper, P.gloves, P.cloths, P.restroomMop],
    contingencies: [C_VOMIT, { if: "Out of toilet paper", then: ["Restock now."], citations: [SP("restrooms restocked")] }],
    dont: ["Never mix bleach with ammonia or acid cleaners.", "Never use restroom cloths or mops in the kitchen."],
    citations: [SCHED("Restrooms"), SHOP("Restrooms"), TABLE("Restroom surfaces")],
    keywords: ["toilet", "urinal", "bathroom", "restroom", "washroom"],
    libraryIds: [L.restroom, L.vomit], requirementIds: [R.toilet, R.tools],
  },
  {
    id: "restroom-sink", name: "Restroom sink and fixtures", area: "restroom", kind: "handwashing",
    howOften: [
      { when: "Before and after each service day or event: clean and disinfect; restock", citation: SCHED("Restrooms") },
      { when: "Check soap and paper towels hourly while open", citation: SP("outdoor barbecue station (restrooms checked hourly for soap and paper towels)") },
    ],
    steps: ["Clean the sink, faucet and counter.", "Disinfect them.", "Refill hand soap and paper towels.", "Check warm water.", "Empty the trash."],
    products: [P.handSoap, P.towels, P.restroom, P.nailBrush],
    contingencies: [
      { if: "Out of soap or paper towels", then: ["Restock now."], citations: ["Food Code 6-301.11", "Food Code 6-301.12"] },
      { if: "No warm water", then: ["Get it fixed; handwashing sinks need warm water."], citations: ["Food Code 5-202.12"] },
    ],
    dont: ["Never use restroom cloths on kitchen surfaces."],
    citations: [SCHED("Restrooms"), "Food Code 6-301.11", "Food Code 6-301.12"],
    keywords: ["restroom sink", "bathroom sink", "soap dispenser", "paper towel", "faucet", "restroom"],
    libraryIds: [L.restroom, L.hands], requirementIds: [R.toilet, R.hands],
  },
  {
    id: "restroom-floor", name: "Restroom floors", area: "restroom", kind: "non_food_contact", materials: ["tile", "vinyl"],
    howOften: [{ when: "Before and after each service day or event", citation: SCHED("Restrooms") }],
    steps: ["Sweep.", "Mop with the restroom-only mop and disinfectant.", "Empty the mop bucket at the mop sink.", "Hang the mop to dry."],
    products: [P.restroomMop, P.restroom],
    contingencies: [C_VOMIT],
    dont: ["Never use the restroom mop in the kitchen.", "Never dump mop water in a food, dish or hand sink.", "No carpet in toilet rooms."],
    citations: [SCHED("Restrooms"), SP("cleaning supplies and chemicals (mops, brooms and buckets)"), SP("facility requirements (floors)")],
    keywords: ["restroom floor", "bathroom floor", "floor", "mop"],
    libraryIds: [L.restroom, L.vomit], requirementIds: [R.tools, R.toilet],
  },

  // ------------------------------------------------------------ FLOORS AND WALLS
  {
    id: "kitchen-floor", name: "Kitchen floors", area: "floors_walls", kind: "non_food_contact", materials: ["tile", "vinyl", "sealed concrete"],
    howOften: [{ when: "After each day or event", citation: SCHED("Floors (kitchen)") }, { when: "Daily after close", citation: SP("master cleaning schedule, home kitchen (Floors)") }],
    steps: ["Cover or put away food first.", "Sweep.", "Mop with detergent (a degreaser for kitchen grease).", "Dump mop water at the mop sink.", "Hang the mop to dry."],
    products: [P.floorKitchen, P.floorTile, P.kitchenMop],
    contingencies: [C_VOMIT, { if: "Spill during service", then: ["Clean it up right away."], citations: [SP("prevention (clean spills immediately)")] }],
    dont: ["Never mop around open food.", "No wax or polish in kitchens (slippery).", "Never use the restroom mop here."],
    citations: [SCHED("Floors (kitchen)"), SHOP("Tile and vinyl floors"), SP("cleaning supplies and chemicals (mops)")],
    keywords: ["floor", "kitchen floor", "mop", "sweep", "tile"],
    libraryIds: [L.restroom], requirementIds: [R.tools],
  },
  {
    id: "dining-floor", name: "Dining room floors (tile, vinyl, carpet)", area: "floors_walls", kind: "non_food_contact", materials: ["tile", "vinyl", "carpet"],
    howOften: [{ when: "Daily after close: sweep and mop", citation: SP("master cleaning schedule, home kitchen (Floors)") }, { when: "Carpet: professional extraction yearly", citation: SHOP("Carpet") }],
    steps: ["Sweep or vacuum.", "Mop tile or vinyl with a neutral floor cleaner.", "Spot-clean carpet stains.", "Dump mop water at the mop sink."],
    products: [P.floorTile, P.carpet],
    contingencies: [C_VOMIT],
    dont: ["Don't mop near open food on tables."],
    citations: [SHOP("Tile and vinyl floors"), SHOP("Carpet")],
    keywords: ["dining room floor", "floor", "carpet", "rug", "lobby floor"],
    libraryIds: [L.restroom], requirementIds: [R.tools],
  },
  {
    id: "hardwood-floor", name: "Hardwood floors", area: "floors_walls", kind: "non_food_contact", materials: ["wood", "hardwood"],
    howOften: [{ when: "Daily after close: sweep, then clean with a hardwood floor cleaner", citation: SP("master cleaning schedule, home kitchen (Floors)") }],
    steps: ["Sweep or dust-mop.", "Clean with a hardwood floor cleaner.", "Wipe up spills right away."],
    products: [P.hardwood],
    contingencies: [C_VOMIT],
    dont: ["No steam mops.", "No wet mopping."],
    citations: [SHOP("Hardwood floors"), SHOP("Floors (hardwood)")],
    keywords: ["hardwood", "wood floor", "hardwood floor", "floor"],
    libraryIds: [L.restroom], requirementIds: [R.tools],
  },
  {
    id: "floor-drains", name: "Floor drains", area: "floors_walls", kind: "non_food_contact",
    howOften: [{ when: "Weekly", citation: SCHED("Floor drains") }],
    steps: ["Take off the cover.", "Scrub the drain and cover.", "Flush with water.", "Put the cover back."],
    products: [P.degreaser, P.spray],
    contingencies: [{ if: "Fruit flies or drain flies", then: ["Clean the drain and the area around it.", "Act within 24 hours on flies breeding."], citations: [SP("action thresholds (flies)"), SP("inspection checklist (floor drains clean with covers in place)")] }],
    dont: ["Never pour grease or coffee grounds down a drain."],
    citations: [SCHED("Floor drains"), SP("inspection checklist (floor drains)")],
    keywords: ["floor drain", "drain", "drain flies", "fruit flies"],
    libraryIds: [L.pests, L.pestSigns], requirementIds: [R.pests],
  },
  {
    id: "walls-ceilings", name: "Walls, ceilings, light fixtures and vents", area: "floors_walls", kind: "non_food_contact",
    howOften: [{ when: "Monthly spot-clean; yearly deep clean", citation: SCHED("Walls, ceilings, light fixtures, vents") }],
    steps: ["Spot-clean splashes and grease with detergent and water.", "Rinse and let it dry.", "Once a year, deep clean walls, ceilings, light fixtures and vents."],
    products: [P.degreaser],
    contingencies: [{ if: "Grease on walls near the stove", then: ["Clean it and check the hood filters."], citations: [SP("facility requirements (ventilation)")] }],
    dont: ["Don't clean over open food."],
    citations: [SCHED("Walls, ceilings, light fixtures, vents"), SP("facility requirements (walls and ceilings)")],
    keywords: ["walls", "ceiling", "light fixture", "vents", "backsplash"],
    libraryIds: [L.clean], requirementIds: [R.freq],
  },
  {
    id: "mop-sink", name: "Mop sink, mops and buckets", area: "floors_walls", kind: "equipment",
    howOften: [{ when: "Every time you mop: fill and empty buckets only at the mop sink; hang mops to dry", citation: SP("cleaning supplies and chemicals (mops, brooms and buckets)") }],
    // Sweep first (master cleaning schedule, floors: "Sweep", then mop); the wet floor sign is OSHA 1910.145(c)(2)
    // (osha-housekeeping OS33, OS41); the fill step names the floor cleaner for the room's floor (roomcheck floorWithWhat).
    steps: ["Sweep the floor first.", "Fill the bucket at the mop sink.", "Put out a wet floor sign.", "Mop.", "Dump dirty water at the mop sink.", "Rinse the bucket.", "Hang the mop to air-dry where it can't drip on food or equipment."],
    products: [P.kitchenMop, P.restroomMop, P.wetFloorSign],
    contingencies: [{ if: "No mop sink", then: ["Fill and empty buckets in a tub or utility sink, never a kitchen, dish or hand sink during service."], citations: [SP("cleaning supplies and chemicals (home kitchen)")] }],
    dont: ["Never dump mop water in a food, dish or hand sink.", "Never store mops in the pantry or walk-in.", "Never use the restroom mop in the kitchen."],
    citations: ["Food Code 5-203.13", "Food Code 6-306.10", "Food Code 6-501.16", SP("cleaning supplies and chemicals"), SCHED("Floors (kitchen)"), "OSHA 29 CFR 1910.145(c)(2)"],
    keywords: ["mop sink", "service sink", "janitor sink", "mop", "bucket", "mop bucket"],
    libraryIds: [L.restroom], requirementIds: [R.tools],
  },

  // ------------------------------------------------------------ TRASH AND OUTSIDE
  {
    id: "trash-cans-indoor", name: "Indoor trash cans", area: "trash_outside", kind: "non_food_contact", materials: ["plastic", "metal"],
    howOften: [{ when: "Emptied outside and washed down at the end of each day the kitchen is used", citation: SCHED("Trash cans") }, { when: "Empty when full", citation: SP("solid waste (remove often)") }],
    steps: ["Tie off the bag and take it straight out to the dumpster.", "Wash the can inside and out with detergent and water, outdoors or at the mop sink.", "Rinse and spray with sanitizer or disinfectant.", "Air-dry.", "Put in a new liner and the lid.", "Wash hands."],
    products: [P.bags, P.dishSoap, P.touchWipes],
    contingencies: [{ if: "Trash is overflowing during service", then: ["Take it out between serving times, not through food prep or serving areas while food is out."], citations: [SP("solid waste (during service)")] }],
    dont: ["Never wash a trash can in a food, dish or hand sink.", "Don't leave cans uncovered."],
    citations: [SCHED("Trash cans"), SP("end-of-day trash routine"), "Food Code 5-5"],
    keywords: ["trash", "trash can", "garbage", "garbage can", "bin", "waste"],
    libraryIds: [L.pests], requirementIds: [R.pests],
  },
  {
    id: "trash-cans-outdoor", name: "Outdoor trash cans and recycling bins", area: "trash_outside", kind: "non_food_contact",
    howOften: [{ when: "Emptied when about three-quarters full at outdoor events; washed down at the end of the day", citation: SP("solid waste (at the BBQ)") }],
    steps: ["Keep lids on.", "Empty before they overflow.", "Wash down outside on a hard surface near a drain.", "Sanitize and air-dry.", "Re-line."],
    products: [P.bags, P.touchWipes],
    contingencies: [{ if: "Flies around the cans", then: ["Empty and wash the cans; keep lids closed."], citations: [SP("flies carry Shigella"), SP("action thresholds (flies)")] }],
    dont: ["Don't place cans within 20 feet of food at outdoor events."],
    citations: [SP("solid waste"), SP("end-of-day trash routine")],
    keywords: ["outdoor trash", "recycling", "recycle bin", "trash can", "garbage"],
    libraryIds: [L.pests], requirementIds: [R.pests],
  },
  {
    id: "dumpster-area", name: "Dumpster area", area: "trash_outside", kind: "non_food_contact",
    howOften: [{ when: "Weekly: sweep, hose, keep lids closed", citation: SCHED("Dumpster area") }, { when: "Dumpster emptied at least weekly", citation: SP("solid waste (remove often)") }],
    steps: ["Close the lids and put the drain plugs in.", "Sweep up litter.", "Hose down the pad.", "Keep bags inside the dumpster, never beside it."],
    products: [P.bags],
    contingencies: [C_PESTS],
    dont: ["Don't leave bags outside the dumpster.", "Keep it away from doors and air intakes."],
    citations: [SCHED("Dumpster area"), SP("solid waste")],
    keywords: ["dumpster", "trash enclosure", "garbage", "outside"],
    libraryIds: [L.pests, L.pestSigns], requirementIds: [R.pests],
  },
  {
    id: "grease-trap", name: "Grease trap and used cooking oil", area: "trash_outside", kind: "equipment",
    howOften: [{ when: "Pumped out per city requirement (often every 1–3 months)", citation: SCHED("Grease trap or interceptor") }],
    steps: ["Pour used oil into a sealed container for grease pickup.", "Scrape grease into the trash, not the sink.", "Schedule the grease hauler.", "Note the grease hauler on the cleaning schedule."],
    products: [],
    contingencies: [{ if: "Grease spilled outside", then: ["Clean it up. Grease and wastewater go to a sanitary sewer drain inside, never onto grass or into a storm drain."], citations: [SP("outdoor barbecue station")] }],
    dont: ["Never pour oil down drains or on the ground."],
    citations: [SCHED("Grease trap or interceptor"), SP("solid waste (used cooking oil)")],
    keywords: ["grease trap", "interceptor", "used oil", "cooking oil", "grease"],
    libraryIds: [L.chem], requirementIds: [R.freq],
  },
];
}

/**
 * The surface guides at a place: its own state's citations and notes (fix/one-truth: Utah's "(Utah amendment)" citations,
 * "hard Utah water" and "Don't use the boiling-water check in Utah" showed in every state). A null word leaves its part out.
 */
export const surfacesFor = perState((st): SurfaceGuide[] => buildSurfaces(wordsFor(WORDS, st)));
/** Utah's guides. Apps read surfacesFor(the place's state). */
export const SURFACES: SurfaceGuide[] = surfacesFor("UT");

// ---------------------------------------------------------------------------------------------------------------
// Lookup and search
// ---------------------------------------------------------------------------------------------------------------

export function getSurface(id: string, state?: UsState): SurfaceGuide | undefined {
  return (state ? surfacesFor(state) : SURFACES).find((s) => s.id === id);
}

const STOP = new Set(["the", "a", "an", "and", "or", "of", "on", "in", "to", "for", "my", "our", "it", "is", "was", "how", "do", "i", "what", "with", "at", "clean", "cleaning", "got", "has", "had", "been", "there", "this", "that", "your"]);

const tokens = (s: string) => s.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 1);
const forms = (w: string) => { const f = [w]; if (w.length > 3 && w.endsWith("s") && !w.endsWith("ss")) f.push(w.slice(0, -1)); if (w.length > 4 && w.endsWith("es")) f.push(w.slice(0, -2)); return f; };
/** A query word matches a text token when they are the same word (singular/plural), or the query word is a prefix of a longer token ("stove" → "stovetop"). */
const matches = (w: string, tok: string) => { const a = forms(w), b = forms(tok); return a.some((x) => b.includes(x)) || (w.length >= 4 && tok.startsWith(w)); };
const queryWords = (q: string) => tokens(q).filter((w) => !STOP.has(w));

export function searchSurfaces(q: string, state?: UsState): SurfaceGuide[] {
  const all = state ? surfacesFor(state) : SURFACES;
  const words = queryWords(q);
  if (!words.length) return all;
  const phrase = words.join(" ");
  const score = (s: SurfaceGuide) => {
    const name = tokens(s.name), hay = tokens([...s.keywords, ...(s.materials ?? [])].join(" "));
    const body = tokens([...s.steps, ...s.contingencies.flatMap((c) => [c.if, ...c.then]), ...s.dont].join(" "));
    let n = 0, hit = 0;
    for (const w of words) {
      const inName = name.some((t) => matches(w, t)), inHay = hay.some((t) => matches(w, t));
      if (inName || inHay) hit++;
      n += inName ? 4 : 0;
      n += inHay ? 3 : 0;
      if (!inName && !inHay && body.some((t) => matches(w, t))) n += 1;
    }
    if (!n) return 0;
    // Everyday phrase match ("steam wand", "wood cutting board") and full coverage of the query words win ties.
    if ([s.name, ...s.keywords].some((k) => tokens(k).join(" ").includes(phrase))) n += 6;
    if (hit === words.length) n += 2;
    return n;
  };
  return all.map((s) => [s, score(s)] as const).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([s]) => s);
}

/** Links a product pick to a reorderable supply when the catalog entry didn't name one (by its purpose). */
const PURPOSE_SUPPLY: [RegExp, string][] = [
  [/dish soap|washing by hand/i, "dish-soap"], [/dishwasher detergent/i, "dish-detergent"], [/floor degreaser|tile and vinyl floor|floor cleaner/i, "floor-cleaner"],
  [/degreaser/i, "degreaser"], [/stainless/i, "stainless-cleaner"], [/glass cleaner/i, "glass-cleaner"], [/granite|stone counter/i, "stone-cleaner"],
  [/wood table|furniture/i, "wood-cleaner"], [/hardwood/i, "hardwood-cleaner"], [/upholstery|carpet/i, "upholstery-cleaner"],
  [/backflush|portafilter soak/i, "espresso-cleaner"], [/steam wand/i, "wand-cleaner"], [/descal/i, "descaler"], [/grinder/i, "grinder-cleaner"],
  [/alcohol swab/i, "alcohol-swabs"], [/nail brush/i, "nail-brush"], [/disinfect|wipes?\b/i, "disinfectant"],
];
export function supplyKeyFor(p: ProductPick): string | undefined {
  return p.supplyKey ?? PURPOSE_SUPPLY.find(([re]) => re.test(p.purpose))?.[1];
}

/** One easy fix: something to DO now, or something to BUY (linked to supplies for one-tap reorder). */
export interface EasyFix { kind: "do" | "buy"; text: string; supplyKey?: string; examples?: string[]; citations: string[]; surfaceId?: string }

/**
 * Easy fixes for a surface and, optionally, a situation matched against its contingencies.
 * Order: contingency steps for the situation first (best match first), then the standard steps, then products to buy.
 */
export function easyFixes(surfaceId: string, situation?: string): EasyFix[] {
  const s = getSurface(surfaceId);
  if (!s) return [];
  const out: EasyFix[] = [];
  const seen = new Set<string>();
  const push = (f: EasyFix) => { const k = `${f.kind}:${f.text}`; if (!seen.has(k)) { seen.add(k); out.push(f); } };
  // Words that only name the surface ("cutting board", "wood") say WHAT it is, not what happened to it, so they never
  // trigger a contingency on their own (scrutiny: "how do I clean a wood cutting board" matched "deeply cut").
  const naming = tokens([s.name, ...(s.materials ?? [])].join(" "));
  const stem = (w: string) => w.slice(0, 5);
  const names = (w: string) => naming.some((x) => matches(w, x) || (w.length >= 5 && x.length >= 5 && stem(w) === stem(x)));
  const words = situation ? queryWords(situation).filter((w) => w.length > 2 && !names(w)) : [];
  if (words.length) {
    const ranked = s.contingencies
      .map((c) => { const t = tokens(c.if); return [c, words.filter((w) => t.some((x) => matches(w, x))).length] as const; })
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
    for (const [c] of ranked) for (const t of c.then) push({ kind: "do", text: t, citations: c.citations, surfaceId: s.id });
  }
  if (!out.length) for (const t of s.steps.slice(0, 5)) push({ kind: "do", text: t, citations: s.citations, surfaceId: s.id });
  for (const p of s.products) push({ kind: "buy", text: `${p.purpose}: ${p.examples.join(" or ")}`, supplyKey: supplyKeyFor(p), examples: p.examples, citations: s.citations, surfaceId: s.id });
  return out;
}
